import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import type { InnovationProjectModel } from './modelsInnovation';
import { useAppStore } from '@/store/appStore';
import { Maximize2, Minimize2, Plus, Trash2, X, AlertTriangle, Eye, FileText, Info, Tags, UploadCloud, Clock, Edit, Database, Check, Download } from 'lucide-react';
import { uploadInnovationProjectFile, deleteInnovationProjectFile, downloadInnovationProjectFileBlob } from './storageService';
import type {
  InnovationProjectImportConfig,
  InnovationUpdateImportConfig,
} from './csvInnovationImport';

// Se eliminaron las constantes estáticas de TOPICS_BY_DRIVER y PROJECTS_BY_TOPIC
// para usar la configuración dinámica desde Firebase.

interface ActionGridInnovationProps {
  innovationData: InnovationProjectModel[];
  onAddInnovationProject: (
    project: Omit<InnovationProjectModel, 'clientId'>
  ) => Promise<void> | void;
  selectedInnovationProject: InnovationProjectModel | null;
  onSelectInnovationProject: (project: InnovationProjectModel) => void;
  onUpdateInnovationProject: (project: InnovationProjectModel) => void;
  onDeleteInnovationProject: (project: InnovationProjectModel) => void;
  onAddInnovationUpdate: (
    project: InnovationProjectModel,
    updateData: any,
    isDelete?: boolean,
    updateIdToDelete?: string
  ) => Promise<void>;
  onCloseInnovationDetails: () => void;
  onSectionChange: (section: 'commercial' | 'innovation' | 'mitigation') => void;
  clients: Record<string, { id: string; name: string }> | { id: string; name: string }[];
  selectedClientId: string;
  countryName: string; // Nuevo prop para pasar el nombre del país seleccionado
  leadTechnicianEmail: string;
  showCommercialActionsButton?: boolean;
  showMitigationButton?: boolean;
}

type ViewMode = 'compact' | 'comfortable' | 'auto';

const padDatePart = (value: number) => String(value).padStart(2, '0');

const getCurrentLocalDateTimeInputValue = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60 * 1000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
};

const getCurrentLocalDateInputValue = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60 * 1000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
};

const formatSapDateTime = (value?: string) => {
  if (!value) return '';

  const digits = value.replace(/\D/g, '');
  if (digits.length >= 12) {
    return digits.slice(0, 14).padEnd(14, '0');
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return '';

  return [
    parsedDate.getFullYear(),
    padDatePart(parsedDate.getMonth() + 1),
    padDatePart(parsedDate.getDate()),
    padDatePart(parsedDate.getHours()),
    padDatePart(parsedDate.getMinutes()),
    padDatePart(parsedDate.getSeconds()),
  ].join('');
};

const parseLegacyUpdateDate = (value: string) => {
  const legacyMatch = value.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,\s*|\s+)(\d{1,2}):(\d{2})(?::(\d{2}))?$/
  );

  if (!legacyMatch) return null;

  const [, day, month, year, hour, minute, second = '00'] = legacyMatch;
  return {
    year,
    month: month.padStart(2, '0'),
    day: day.padStart(2, '0'),
    hour: hour.padStart(2, '0'),
    minute,
    second,
  };
};

const sapDateTimeToInputValue = (value?: string) => {
  if (!value) return '';

  const digits = value.replace(/\D/g, '');
  if (digits.length >= 12) {
    const normalized = digits.slice(0, 14).padEnd(14, '0');
    return `${normalized.slice(0, 4)}-${normalized.slice(4, 6)}-${normalized.slice(6, 8)}T${normalized.slice(8, 10)}:${normalized.slice(10, 12)}`;
  }

  const legacyDate = parseLegacyUpdateDate(value);
  if (legacyDate) {
    return `${legacyDate.year}-${legacyDate.month}-${legacyDate.day}T${legacyDate.hour}:${legacyDate.minute}`;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return '';

  return `${parsedDate.getFullYear()}-${padDatePart(parsedDate.getMonth() + 1)}-${padDatePart(parsedDate.getDate())}T${padDatePart(parsedDate.getHours())}:${padDatePart(parsedDate.getMinutes())}`;
};

const formatUpdateDateForDisplay = (value?: string) => {
  if (!value) return '';

  const digits = value.replace(/\D/g, '');
  if (digits.length >= 12) {
    const normalized = digits.slice(0, 14).padEnd(14, '0');
    return `${normalized.slice(6, 8)}/${normalized.slice(4, 6)}/${normalized.slice(0, 4)} ${normalized.slice(8, 10)}:${normalized.slice(10, 12)}:${normalized.slice(12, 14)}`;
  }

  return value;
};

export function ActionGridInnovation({
  innovationData,
  onAddInnovationProject,
  selectedInnovationProject,
  onSelectInnovationProject,
  onUpdateInnovationProject,
  onDeleteInnovationProject,
  onAddInnovationUpdate,
  onCloseInnovationDetails,
  onSectionChange,
  clients,
  selectedClientId,
  countryName,
  leadTechnicianEmail,
  showCommercialActionsButton = true,
  showMitigationButton = true,
}: ActionGridInnovationProps) {
  const gridRef = useRef<AgGridReact>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('comfortable');
  const canEdit = true;

  useEffect(() => {
    setViewMode('comfortable');
  }, []);

  // Estado para la configuración dinámica de Dropdowns
  const [dropdownConfig, setDropdownConfig] = useState<InnovationProjectImportConfig>({
    drivers: [],
    topicsByDriver: {},
    projectsByTopic: {},
    driverIdsByName: {},
    topicIdsByName: {},
    projectIdsByName: {}
  });

  const [updateDropdownConfig, setUpdateDropdownConfig] = useState<InnovationUpdateImportConfig & {
    statusNamesById: Record<string, string>;
    departmentNamesById: Record<string, string>;
    plcStageNamesById: Record<string, string>;
  }>({
    statuses: [],
    departments: [],
    plcStages: [],
    statusIdsByName: {},
    departmentIdsByName: {},
    plcStageIdsByName: {},
    statusNamesById: {},
    departmentNamesById: {},
    plcStageNamesById: {},
  });

  const fetchInnovationConfigs = useCallback(async () => {
    const {
      getInnovationDropdownConfig,
      getInnovationUpdateDropdownConfig,
    } = await import('./firebaseInnovationService');

    const [projectConfig, updateConfig] = await Promise.all([
      getInnovationDropdownConfig(),
      getInnovationUpdateDropdownConfig(),
    ]);

    setDropdownConfig(projectConfig);
    setUpdateDropdownConfig(updateConfig);
  }, []);

  useEffect(() => {
    fetchInnovationConfigs().catch((error) => {
      console.error('Error fetching innovation config', error);
    });
  }, [fetchInnovationConfigs]);

  const { setError } = useAppStore();

  const clientsArray = useMemo(() => {
    if (!clients) return [];
    if (Array.isArray(clients)) return clients;
    return Object.values(clients);
  }, [clients]);

  const selectedClientName = useMemo(() => {
    if (!selectedClientId || selectedClientId === 'all') return '';
    if (Array.isArray(clients)) {
      const match = clients.find(c => c.id === selectedClientId);
      return match?.name || '';
    }
    const record: Record<string, any> = clients as any;
    const fromKey = record[selectedClientId];
    if (fromKey) {
      return fromKey.name || fromKey.id || '';
    }
    const byId = Object.values(record).find((c: any) => c.id === selectedClientId);
    return (byId as any)?.name || '';
  }, [clients, selectedClientId]);

  const [selectedRows, setSelectedRows] = useState<InnovationProjectModel[]>([]);
  const handleEditSelectedProject = useCallback(() => {
    if (selectedRows.length === 1) {
      onSelectInnovationProject(selectedRows[0]);
    }
  }, [onSelectInnovationProject, selectedRows]);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [isAddInnovationModalOpen, setIsAddInnovationModalOpen] = useState(false);
  const [isInnovationDeleteModalOpen, setIsInnovationDeleteModalOpen] = useState(false);
  const [showUnsavedChangesWarning, setShowUnsavedChangesWarning] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{ url: string, idx?: number, isLegacy?: boolean, isUpdate?: boolean } | null>(null);
  const [innovationForm, setInnovationForm] = useState({
    topic: '',
    project: '',
    subproject: '',
    driver: '',
    priority: 'Medium' as InnovationProjectModel['priority'],
    customer: '',
    context: '',
    country: '', // Añadido para el selector de país
    projectDescription: '',
    leadTechnician: leadTechnicianEmail,
    creationDate: getCurrentLocalDateInputValue(),
    fileName: '',
    status: 'On Hold',
  });

  useEffect(() => {
    if (!isAddInnovationModalOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsAddInnovationModalOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAddInnovationModalOpen]);

  useEffect(() => {
    if (!isAddInnovationModalOpen) return;
    if (selectedClientId === 'all') {
       // Si el cliente es "all", no sobreescribimos con "unassigned" o vacío automáticamente,
       // permitiendo al usuario elegir el cliente manualmente en el formulario.
       return;
    }
    if (!selectedClientName) return;
    setInnovationForm(prev => ({
      ...prev,
      customer: selectedClientName,
    }));
  }, [isAddInnovationModalOpen, selectedClientId, selectedClientName]);

  const [innovationDetailsForm, setInnovationDetailsForm] = useState<InnovationProjectModel | null>(null);
  const [selectedFilesForUpload, setSelectedFilesForUpload] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [missingCreateFields, setMissingCreateFields] = useState<string[]>([]);
  const [isDropdownItemModalOpen, setIsDropdownItemModalOpen] = useState(false);
  const [dropdownItemModalMode, setDropdownItemModalMode] = useState<'driver' | 'topic' | 'project'>('driver');
  const [dropdownItemModalTarget, setDropdownItemModalTarget] = useState<'create' | 'details'>('create');
  const [dropdownItemModalState, setDropdownItemModalState] = useState({
    driverName: '',
    topicTrees: [{ topic: '', projects: [''] }],
    projectOptions: [''],
  });
  const [isDropdownRenameModalOpen, setIsDropdownRenameModalOpen] = useState(false);
  const [dropdownRenameModalMode, setDropdownRenameModalMode] = useState<'driver' | 'topic' | 'project'>('driver');
  const [dropdownRenameModalTarget, setDropdownRenameModalTarget] = useState<'create' | 'details'>('create');
  const [dropdownRenameModalOldName, setDropdownRenameModalOldName] = useState('');
  const [dropdownRenameModalNewName, setDropdownRenameModalNewName] = useState('');

  const openDropdownItemModal = useCallback(
    (mode: 'driver' | 'topic' | 'project', target: 'create' | 'details') => {
      setDropdownItemModalMode(mode);
      setDropdownItemModalTarget(target);
      setDropdownItemModalState({
        driverName: '',
        topicTrees: mode === 'project' ? [] : [{ topic: '', projects: [''] }],
        projectOptions: [''],
      });
      setIsDropdownItemModalOpen(true);
    },
    []
  );

  const updateProjectOption = useCallback((index: number, value: string) => {
    setDropdownItemModalState((prev) => {
      const list = prev.projectOptions.slice();
      list[index] = value;
      return { ...prev, projectOptions: list };
    });
  }, []);

  const addProjectOptionRow = useCallback(() => {
    setDropdownItemModalState((prev) => ({ ...prev, projectOptions: [...prev.projectOptions, ''] }));
  }, []);

  const removeProjectOptionRow = useCallback((index: number) => {
    setDropdownItemModalState((prev) => {
      const list = prev.projectOptions.filter((_, i) => i !== index);
      return { ...prev, projectOptions: list.length > 0 ? list : [''] };
    });
  }, []);

  const addTopicTreeRow = useCallback(() => {
    setDropdownItemModalState((prev) => ({
      ...prev,
      topicTrees: [...prev.topicTrees, { topic: '', projects: [''] }],
    }));
  }, []);

  const removeTopicTreeRow = useCallback((topicIndex: number) => {
    setDropdownItemModalState((prev) => {
      const next = prev.topicTrees.filter((_, i) => i !== topicIndex);
      return { ...prev, topicTrees: next.length > 0 ? next : [{ topic: '', projects: [''] }] };
    });
  }, []);

  const updateTopicTreeName = useCallback((topicIndex: number, value: string) => {
    setDropdownItemModalState((prev) => {
      const topicTrees = prev.topicTrees.slice();
      const current = topicTrees[topicIndex];
      if (!current) return prev;
      topicTrees[topicIndex] = { ...current, topic: value };
      return { ...prev, topicTrees };
    });
  }, []);

  const addTopicTreeProjectRow = useCallback((topicIndex: number) => {
    setDropdownItemModalState((prev) => {
      const topicTrees = prev.topicTrees.slice();
      const current = topicTrees[topicIndex];
      if (!current) return prev;
      topicTrees[topicIndex] = { ...current, projects: [...current.projects, ''] };
      return { ...prev, topicTrees };
    });
  }, []);

  const removeTopicTreeProjectRow = useCallback((topicIndex: number, projectIndex: number) => {
    setDropdownItemModalState((prev) => {
      const topicTrees = prev.topicTrees.slice();
      const current = topicTrees[topicIndex];
      if (!current) return prev;
      const projects = current.projects.filter((_, i) => i !== projectIndex);
      topicTrees[topicIndex] = { ...current, projects: projects.length > 0 ? projects : [''] };
      return { ...prev, topicTrees };
    });
  }, []);

  const updateTopicTreeProjectName = useCallback((topicIndex: number, projectIndex: number, value: string) => {
    setDropdownItemModalState((prev) => {
      const topicTrees = prev.topicTrees.slice();
      const current = topicTrees[topicIndex];
      if (!current) return prev;
      const projects = current.projects.slice();
      projects[projectIndex] = value;
      topicTrees[topicIndex] = { ...current, projects };
      return { ...prev, topicTrees };
    });
  }, []);

  const closeDropdownItemModal = useCallback(() => {
    setIsDropdownItemModalOpen(false);
  }, []);

  const closeDropdownRenameModal = useCallback(() => {
    setIsDropdownRenameModalOpen(false);
  }, []);

  const openDropdownRenameModal = useCallback(
    (mode: 'driver' | 'topic' | 'project', target: 'create' | 'details') => {
      const isCreate = target === 'create';
      const driverName = isCreate ? innovationForm.driver : (innovationDetailsForm?.driver || '');
      const topicName = isCreate ? innovationForm.topic : (innovationDetailsForm?.topic || '');
      const projectName = isCreate ? innovationForm.project : (innovationDetailsForm?.project || '');

      const oldName =
        mode === 'driver' ? driverName : mode === 'topic' ? topicName : projectName;

      setDropdownRenameModalMode(mode);
      setDropdownRenameModalTarget(target);
      setDropdownRenameModalOldName(oldName || '');
      setDropdownRenameModalNewName(oldName || '');
      setIsDropdownRenameModalOpen(true);
    },
    [innovationDetailsForm?.driver, innovationDetailsForm?.project, innovationDetailsForm?.topic, innovationForm.driver, innovationForm.project, innovationForm.topic]
  );

  const handleRenameDropdownItem = useCallback(async () => {
    const normalize = (value: string) => value.trim().replace(/\s+/g, ' ');
    const oldName = dropdownRenameModalOldName;
    const newName = normalize(dropdownRenameModalNewName);
    const targetIsCreate = dropdownRenameModalTarget === 'create';
    const currentDriverName = targetIsCreate ? innovationForm.driver : (innovationDetailsForm?.driver || '');
    const currentTopicName = targetIsCreate ? innovationForm.topic : (innovationDetailsForm?.topic || '');

    if (!oldName) {
      setError('Select an item to edit.');
      return;
    }
    if (!newName) {
      setError('New name is required.');
      return;
    }
    if (newName.toLowerCase() === oldName.trim().toLowerCase()) {
      closeDropdownRenameModal();
      return;
    }

    try {
      const {
        renameInnovationDriver,
        renameInnovationTopic,
        renameInnovationProjectOption,
      } = await import('./firebaseInnovationService');

      if (dropdownRenameModalMode === 'driver') {
        const driverId = dropdownConfig.driverIdsByName[oldName];
        if (!driverId) {
          setError('Driver id not found.');
          return;
        }
        const existingDriverId = dropdownConfig.driverIdsByName[newName];
        if (existingDriverId) {
          setError('A driver with that name already exists.');
          return;
        }
        await renameInnovationDriver(driverId, newName);
        await fetchInnovationConfigs();
        if (targetIsCreate) {
          setInnovationForm((prev) => ({ ...prev, driver: newName }));
        } else {
          setInnovationDetailsForm((prev) => (prev ? { ...prev, driver: newName } : prev));
        }
        closeDropdownRenameModal();
        return;
      }

      if (dropdownRenameModalMode === 'topic') {
        if (!currentDriverName) {
          setError('Select a driver first.');
          return;
        }
        const driverId = dropdownConfig.driverIdsByName[currentDriverName];
        if (!driverId) {
          setError('Driver id not found.');
          return;
        }
        const topicId = dropdownConfig.topicIdsByName[oldName];
        if (!topicId) {
          setError('Topic id not found.');
          return;
        }
        const existingTopics = dropdownConfig.topicsByDriver[currentDriverName] || [];
        const topicExists = existingTopics.some((t) => t.trim().toLowerCase() === newName.toLowerCase());
        if (topicExists) {
          setError('A topic with that name already exists in this driver.');
          return;
        }
        await renameInnovationTopic(driverId, topicId, newName);
        await fetchInnovationConfigs();
        if (targetIsCreate) {
          setInnovationForm((prev) => ({ ...prev, topic: newName }));
        } else {
          setInnovationDetailsForm((prev) => (prev ? { ...prev, topic: newName } : prev));
        }
        closeDropdownRenameModal();
        return;
      }

      if (!currentTopicName) {
        setError('Select a topic first.');
        return;
      }
      const topicId = dropdownConfig.topicIdsByName[currentTopicName];
      if (!topicId) {
        setError('Topic id not found.');
        return;
      }
      const projectId = dropdownConfig.projectIdsByName[oldName];
      if (!projectId) {
        setError('Project id not found.');
        return;
      }
      const existingProjects = dropdownConfig.projectsByTopic[currentTopicName] || [];
      const projectExists = existingProjects.some((p) => p.trim().toLowerCase() === newName.toLowerCase());
      if (projectExists) {
        setError('A project with that name already exists in this topic.');
        return;
      }
      await renameInnovationProjectOption(topicId, projectId, newName);
      await fetchInnovationConfigs();
      if (targetIsCreate) {
        setInnovationForm((prev) => ({ ...prev, project: newName }));
      } else {
        setInnovationDetailsForm((prev) => (prev ? { ...prev, project: newName } : prev));
      }
      closeDropdownRenameModal();
    } catch (error: any) {
      setError(error?.message || 'Error editing dropdown item.');
    }
  }, [
    closeDropdownRenameModal,
    dropdownConfig.driverIdsByName,
    dropdownConfig.projectIdsByName,
    dropdownConfig.projectsByTopic,
    dropdownConfig.topicIdsByName,
    dropdownConfig.topicsByDriver,
    dropdownRenameModalMode,
    dropdownRenameModalNewName,
    dropdownRenameModalOldName,
    dropdownRenameModalTarget,
    fetchInnovationConfigs,
    innovationDetailsForm?.driver,
    innovationDetailsForm?.topic,
    innovationForm.driver,
    innovationForm.topic,
    setError,
  ]);

  const handleCreateDropdownItem = useCallback(async () => {
    const normalize = (value: string) => value.trim().replace(/\s+/g, ' ');
    const parseOptions = (values: string[]) => {
      const seen = new Set<string>();
      const items = values
        .map((v) => normalize(v))
        .filter(Boolean)
        .filter((item) => {
          const key = item.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      return items;
    };
    const parseTopicTrees = (trees: { topic: string; projects: string[] }[]) => {
      const seenTopics = new Set<string>();
      const items = trees
        .map((node) => ({
          topic: normalize(node.topic),
          projects: parseOptions(node.projects),
        }))
        .filter((node) => node.topic && node.projects.length > 0)
        .filter((node) => {
          const key = node.topic.toLowerCase();
          if (seenTopics.has(key)) return false;
          seenTopics.add(key);
          return true;
        });
      return items;
    };
    const targetIsCreate = dropdownItemModalTarget === 'create';
    const currentDriverName = targetIsCreate ? innovationForm.driver : (innovationDetailsForm?.driver || '');
    const currentTopicName = targetIsCreate ? innovationForm.topic : (innovationDetailsForm?.topic || '');

    try {
      const {
        createInnovationDriver,
        createInnovationTopic,
        createInnovationProjectOption,
      } = await import('./firebaseInnovationService');

      if (dropdownItemModalMode === 'driver') {
        const driverName = normalize(dropdownItemModalState.driverName);
        const topicTrees = parseTopicTrees(dropdownItemModalState.topicTrees);

        if (!driverName) {
          setError('Driver name is required.');
          return;
        }
        if (topicTrees.length === 0) {
          setError('At least 1 topic and 1 project per topic are required when creating a driver.');
          return;
        }

        const { driverId } = await createInnovationDriver(driverName);
        let createdTopicName = '';
        let createdProjectName = '';

        for (const node of topicTrees) {
          const { topicId } = await createInnovationTopic(driverId, node.topic);
          for (const projectName of node.projects) {
            await createInnovationProjectOption(topicId, projectName);
          }
        }

        createdTopicName = topicTrees[0]?.topic || '';
        createdProjectName = topicTrees[0]?.projects[0] || '';

        await fetchInnovationConfigs();

        if (targetIsCreate) {
          setInnovationForm((prev) => ({
            ...prev,
            driver: driverName,
            topic: createdTopicName,
            project: createdProjectName,
          }));
        } else {
          setInnovationDetailsForm((prev) =>
            prev
              ? {
                  ...prev,
                  driver: driverName,
                  topic: createdTopicName || prev.topic,
                  project: createdProjectName || prev.project,
                }
              : prev
          );
        }

        closeDropdownItemModal();
        return;
      }

      if (dropdownItemModalMode === 'topic') {
        const topicTrees = parseTopicTrees(dropdownItemModalState.topicTrees);

        if (!currentDriverName) {
          setError('Select a driver first.');
          return;
        }
        if (topicTrees.length === 0) {
          setError('At least 1 topic and 1 project per topic are required.');
          return;
        }

        const driverId = dropdownConfig.driverIdsByName[currentDriverName];
        if (!driverId) {
          setError('Driver id not found for the selected driver.');
          return;
        }

        let createdProjectName = '';
        for (const node of topicTrees) {
          const { topicId } = await createInnovationTopic(driverId, node.topic);
          for (const projectName of node.projects) {
            await createInnovationProjectOption(topicId, projectName);
          }
        }
        createdProjectName = topicTrees[0]?.projects[0] || '';

        await fetchInnovationConfigs();

        if (targetIsCreate) {
          setInnovationForm((prev) => ({
            ...prev,
            topic: topicTrees[0]?.topic || '',
            project: createdProjectName,
          }));
        } else {
          setInnovationDetailsForm((prev) =>
            prev
              ? { ...prev, topic: topicTrees[0]?.topic || '', project: createdProjectName || prev.project }
              : prev
          );
        }

        closeDropdownItemModal();
        return;
      }

      const projects = parseOptions(dropdownItemModalState.projectOptions);
      if (!currentTopicName) {
        setError('Select a topic first.');
        return;
      }
      if (projects.length === 0) {
        setError('At least 1 project is required.');
        return;
      }

      const topicId = dropdownConfig.topicIdsByName[currentTopicName];
      if (!topicId) {
        setError('Topic id not found for the selected topic.');
        return;
      }

      for (const projectName of projects) {
        await createInnovationProjectOption(topicId, projectName);
      }
      await fetchInnovationConfigs();

      if (targetIsCreate) {
        setInnovationForm((prev) => ({ ...prev, project: projects[0] }));
      } else {
        setInnovationDetailsForm((prev) => (prev ? { ...prev, project: projects[0] } : prev));
      }

      closeDropdownItemModal();
    } catch (error: any) {
      setError(error?.message || 'Error creating dropdown item.');
    }
  }, [
    addProjectOptionRow,
    addTopicTreeProjectRow,
    addTopicTreeRow,
    closeDropdownItemModal,
    dropdownConfig.driverIdsByName,
    dropdownConfig.topicIdsByName,
    dropdownItemModalMode,
    dropdownItemModalState.driverName,
    dropdownItemModalState.projectOptions,
    dropdownItemModalState.topicTrees,
    dropdownItemModalTarget,
    fetchInnovationConfigs,
    innovationDetailsForm?.driver,
    innovationDetailsForm?.topic,
    innovationForm.driver,
    innovationForm.topic,
    removeProjectOptionRow,
    removeTopicTreeProjectRow,
    removeTopicTreeRow,
    setError,
    updateProjectOption,
    updateTopicTreeName,
    updateTopicTreeProjectName,
  ]);

  const handleAddInnovationProject = useCallback(async () => {
    const missingFields: string[] = [];
    if (!innovationForm.driver) missingFields.push('Driver');
    if (!innovationForm.topic.trim()) missingFields.push('Topic');
    if (!innovationForm.project.trim()) missingFields.push('Project');
    if (!innovationForm.subproject.trim()) missingFields.push('Subproject');
    if (!innovationForm.context.trim()) missingFields.push('Context');

    if (missingFields.length > 0) {
      setMissingCreateFields(missingFields);
      return;
    }
    setMissingCreateFields([]);
    
    setIsUploading(true);

    try {
      // Creamos un ID único de antemano para organizar la carpeta en Storage
      // y para que coincida exactamente con el documento en Firestore
      const projectDocId = `INN-${Date.now()}`;

      const driverCode = dropdownConfig.driverIdsByName[innovationForm.driver] || null;
      const topicCode = dropdownConfig.topicIdsByName[innovationForm.topic] || null;
      const projectCode = dropdownConfig.projectIdsByName[innovationForm.project] || null;

      const finalCountry = innovationForm.country || '';
      const finalCustomer = innovationForm.customer.trim() || 'No Client';
      const finalCreationDate =
        innovationForm.creationDate?.trim()?.replace(/-/g, '') ||
        getCurrentLocalDateInputValue().replace(/-/g, '');

      // Create the base project object
      const newProject = {
        id: projectDocId,
        topicId: topicCode || undefined,
        topic: innovationForm.topic.trim(),
        projectId: projectCode || undefined,
        project: innovationForm.project.trim(),
        subproject: innovationForm.subproject.trim(),
        driverId: driverCode || undefined,
        driver: innovationForm.driver,
        context: innovationForm.context,
        priority: innovationForm.priority,
        customer: finalCustomer,
        country: finalCountry, // Pasamos el país seleccionado
        projectDescription: innovationForm.projectDescription.trim(),
        leadTechnician: innovationForm.leadTechnician.trim() || leadTechnicianEmail,
        creationDate: finalCreationDate,
        status: innovationForm.status || undefined,
        idStatus: innovationForm.status ? updateDropdownConfig.statusIdsByName[innovationForm.status] : undefined,
        files: [] as {name: string, url: string}[],
      };

      // Si hay archivos seleccionados, los subimos a Firebase Storage.
      if (selectedFilesForUpload.length > 0) {
        const uploadPromises = selectedFilesForUpload.map(async (file) => {
          const downloadUrl = await uploadInnovationProjectFile(file, projectDocId);
          return { name: file.name, url: downloadUrl };
        });
        
        const uploadedFiles = await Promise.all(uploadPromises);
        newProject.files = uploadedFiles;
      }

      await onAddInnovationProject(newProject);
      
      // Limpiamos los estados
      setInnovationForm({
        topic: '',
        project: '',
        subproject: '',
        driver: '',
        priority: 'Medium',
        customer: '',
        context: '',
        country: '',
        projectDescription: '',
        leadTechnician: leadTechnicianEmail,
        creationDate: getCurrentLocalDateInputValue(),
        fileName: '',
        status: 'On Hold',
      });
      setSelectedFilesForUpload([]);
      setIsAddInnovationModalOpen(false);
      
      // Show success notification
      setShowSaveSuccess(true);
      setTimeout(() => {
        setShowSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error creating project and uploading files:", error);
      const message =
        error instanceof Error ? error.message : 'Error creating innovation project';
      setError(message);
    } finally {
      setIsUploading(false);
    }
  }, [innovationForm, selectedFilesForUpload, onAddInnovationProject, setError]);

  const [isAddUpdateModalOpen, setIsAddUpdateModalOpen] = useState(false);
  const [selectedUpdateView, setSelectedUpdateView] = useState<any>(null);
  const [isUpdateDeleteModalOpen, setIsUpdateDeleteModalOpen] = useState(false);
  const [isUpdateViewerModalOpen, setIsUpdateViewerModalOpen] = useState(false);
  const [isFileViewerModalOpen, setIsFileViewerModalOpen] = useState(false);
  const [updateViewerProject] = useState<InnovationProjectModel | null>(null);
  const [updateViewerList] = useState<any[]>([]);
  const [updateViewerSelectedId, setUpdateViewerSelectedId] = useState<string>('');
  const [initialInnovationDetailsForm, setInitialInnovationDetailsForm] = useState<InnovationProjectModel | null>(null);
  const [innovationUpdateForm, setInnovationUpdateForm] = useState({
    updateDate: '',
    updatedByEmail: leadTechnicianEmail,
    newStatus: '',
    pendingDepartment: '',
    plcStage: '',
    developmentProgress: 0,
    technicalNote: '',
    originalUpdateDate: '', // Used to identify which update we are editing
  });
  
  const [selectedUpdateFiles, setSelectedUpdateFiles] = useState<File[]>([]);

  useEffect(() => {
    if (selectedInnovationProject) {
      setInnovationDetailsForm(selectedInnovationProject);
      setInitialInnovationDetailsForm(selectedInnovationProject);
      setInnovationUpdateForm({
        updateDate: getCurrentLocalDateTimeInputValue(),
        updatedByEmail: leadTechnicianEmail,
        newStatus: '',
        pendingDepartment: '',
        plcStage: '',
        developmentProgress: 0,
        technicalNote: '',
        originalUpdateDate: '',
      });
      setSelectedUpdateFiles([]);
    } else {
      setInnovationDetailsForm(null);
      setInitialInnovationDetailsForm(null);
      setSelectedFilesForUpload([]);
    }
  }, [selectedInnovationProject]);

  const hasUnsavedChanges = useMemo(() => {
    if (selectedFilesForUpload.length > 0) return true;
    if (!innovationDetailsForm || !initialInnovationDetailsForm) return false;
    
    // Comparar omitiendo la propiedad files ya que la manejamos arriba
    const formWithoutFiles = { ...innovationDetailsForm, files: undefined };
    const initialWithoutFiles = { ...initialInnovationDetailsForm, files: undefined };
    
    return JSON.stringify(formWithoutFiles) !== JSON.stringify(initialWithoutFiles);
  }, [innovationDetailsForm, initialInnovationDetailsForm, selectedFilesForUpload]);

  const resetSelectedProjectState = useCallback(() => {
    setSelectedRows([]);
    gridRef.current?.api?.deselectAll();
  }, []);

  useEffect(() => {
    if (!selectedInnovationProject) {
      resetSelectedProjectState();
    }
  }, [resetSelectedProjectState, selectedInnovationProject]);

  const handleBackToList = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesWarning(true);
    } else {
      onCloseInnovationDetails();
      resetSelectedProjectState();
    }
  }, [hasUnsavedChanges, onCloseInnovationDetails, resetSelectedProjectState]);

  const handleConfirmDiscardChanges = useCallback(() => {
    setShowUnsavedChangesWarning(false);
    onCloseInnovationDetails();
    resetSelectedProjectState();
  }, [onCloseInnovationDetails, resetSelectedProjectState]);

  const handleConfirmDeleteFile = useCallback(async () => {
    if (!fileToDelete) return;
    if (!canEdit) {
      setError('Read-only access. Editing is disabled for your role.');
      setFileToDelete(null);
      return;
    }

    try {
      await deleteInnovationProjectFile(fileToDelete.url);
      
      // Update logic depending on if it's an innovation project file or an update file
      if (fileToDelete.isUpdate) {
        if (selectedUpdateView) {
          const updatedView = { ...selectedUpdateView };
          if (fileToDelete.isLegacy) {
            updatedView.file = '';
          } else if (fileToDelete.idx !== undefined) {
            updatedView.files = updatedView.files?.filter((_: any, i: number) => i !== fileToDelete.idx) || [];
          }
          setSelectedUpdateView(updatedView);
        }
      } else {
        setInnovationDetailsForm(prev => {
          if (!prev) return prev;
          
          if (fileToDelete.isLegacy) {
            // Remove legacy file
            return { ...prev, fileName: '', fileUrl: '' };
          } else if (fileToDelete.idx !== undefined) {
            // Remove from files array
            const newFiles = prev.files?.filter((_, i) => i !== fileToDelete.idx) || [];
            return { ...prev, files: newFiles };
          }
          
          return prev;
        });

        // Update in Firestore to reflect the deletion immediately for project files
        if (innovationDetailsForm) {
          let updatedForm = { ...innovationDetailsForm };
          if (fileToDelete.isLegacy) {
            updatedForm.fileName = '';
            updatedForm.fileUrl = '';
          } else if (fileToDelete.idx !== undefined) {
            updatedForm.files = updatedForm.files?.filter((_, i) => i !== fileToDelete.idx) || [];
          }
          onUpdateInnovationProject(updatedForm);
        }
      }
    } catch (error) {
      console.error("Error deleting file from Storage:", error);
    } finally {
      setFileToDelete(null);
    }
  }, [fileToDelete, innovationDetailsForm, onUpdateInnovationProject, selectedUpdateView, canEdit, setError]);

  const handleSaveInnovationDetails = useCallback(async () => {
    if (!innovationDetailsForm) return;
    if (!canEdit) {
      setError('Read-only access. Editing is disabled for your role.');
      return;
    }

    try {
      setIsUploading(true);
      let updatedForm = { ...innovationDetailsForm };

      const driverCode = updatedForm.driver
        ? dropdownConfig.driverIdsByName[updatedForm.driver] || updatedForm.driverId
        : updatedForm.driverId;
      const topicCode = updatedForm.topic
        ? dropdownConfig.topicIdsByName[updatedForm.topic] || updatedForm.topicId
        : updatedForm.topicId;
      const projectCode = updatedForm.project
        ? dropdownConfig.projectIdsByName[updatedForm.project] || updatedForm.projectId
        : updatedForm.projectId;

      if (driverCode) updatedForm.driverId = driverCode;
      if (topicCode) updatedForm.topicId = topicCode;
      if (projectCode) updatedForm.projectId = projectCode;

      // Ensure files array exists
      if (!updatedForm.files) {
        updatedForm.files = [];
      }

      // Si hay archivos seleccionados, los subimos a Firebase Storage
      if (selectedFilesForUpload.length > 0 && updatedForm.id) {
        const uploadPromises = selectedFilesForUpload.map(async (file) => {
          const downloadUrl = await uploadInnovationProjectFile(file, updatedForm.id as string);
          return { name: file.name, url: downloadUrl };
        });
        
        const uploadedFiles = await Promise.all(uploadPromises);
        updatedForm.files = [...updatedForm.files, ...uploadedFiles];
      }

      onUpdateInnovationProject(updatedForm);
      
      // Reset state and close
      setSelectedFilesForUpload([]);
      setIsUploading(false);
      onCloseInnovationDetails();
      resetSelectedProjectState();
      
      // Show success notification
      setShowSaveSuccess(true);
      setTimeout(() => {
        setShowSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error saving project details:", error);
      setIsUploading(false);
      // Opcional: Mostrar un toast de error aquí
    }
  }, [innovationDetailsForm, selectedFilesForUpload, onUpdateInnovationProject, onCloseInnovationDetails, resetSelectedProjectState, canEdit, setError]);

  const handleDeleteInnovation = useCallback(() => {
    if (!canEdit) {
      setError('Read-only access. Editing is disabled for your role.');
      return;
    }
    setIsInnovationDeleteModalOpen(true);
  }, [canEdit, setError]);

  const handleConfirmDeleteInnovation = useCallback(() => {
    if (!canEdit) {
      setError('Read-only access. Editing is disabled for your role.');
      return;
    }
    if (!selectedInnovationProject || !selectedInnovationProject.id) return;
    onDeleteInnovationProject(selectedInnovationProject);
    setIsInnovationDeleteModalOpen(false);
    onCloseInnovationDetails();
    resetSelectedProjectState();
  }, [selectedInnovationProject, onDeleteInnovationProject, onCloseInnovationDetails, resetSelectedProjectState, canEdit, setError]);

  const handleSaveInnovationUpdate = useCallback(async () => {
    if (!canEdit) {
      setError('Read-only access. Editing is disabled for your role.');
      return;
    }
    if (!selectedInnovationProject || !selectedInnovationProject.id) return;
    
    setIsUploading(true);
    
    try {
      const baseStatus = innovationUpdateForm.newStatus || '';
      const baseDept = innovationUpdateForm.pendingDepartment || '';
      const progress = innovationUpdateForm.developmentProgress || 0;

      const sapUpdateDate = formatSapDateTime(
        innovationUpdateForm.updateDate || getCurrentLocalDateTimeInputValue()
      );

      let newUpdateObj: any = {
        updateDate: sapUpdateDate,
        idProjectRef: selectedInnovationProject.id || selectedInnovationProject.project || '',
        createdByEmail: selectedUpdateView?.createdByEmail || innovationUpdateForm.updatedByEmail || leadTechnicianEmail || undefined,
        updatedByEmail: innovationUpdateForm.updatedByEmail || leadTechnicianEmail || undefined,
        newStatus: baseStatus,
        idStatus: baseStatus ? updateDropdownConfig.statusIdsByName[baseStatus] : undefined,
        pendingDepartment: baseDept,
        idDepartment: baseDept ? updateDropdownConfig.departmentIdsByName[baseDept] : undefined,
        plcStage: innovationUpdateForm.plcStage || '',
        idPLC: innovationUpdateForm.plcStage ? updateDropdownConfig.plcStageIdsByName[innovationUpdateForm.plcStage] : undefined,
        developmentProgress: progress,
        technicalNote: innovationUpdateForm.technicalNote || '',
        files: selectedUpdateView?.files ? [...selectedUpdateView.files] : [], // Mantener archivos existentes
        id: selectedUpdateView?.id || innovationUpdateForm.originalUpdateDate || undefined // Pasamos el ID original si estamos editando
      };

      // Si hay archivos nuevos, los subimos a Firebase Storage
      if (selectedUpdateFiles.length > 0) {
        // Usamos la fecha SAP como identificador de carpeta del update
        const updateIdFolder = sapUpdateDate || `UPD-${Date.now()}`;
        
        const uploadPromises = selectedUpdateFiles.map(async (file) => {
          // Importar uploadInnovationUpdateFile al principio del archivo si no está
          const { uploadInnovationUpdateFile } = await import('./storageService');
          const downloadUrl = await uploadInnovationUpdateFile(file, selectedInnovationProject.id as string, updateIdFolder);
          return { name: file.name, url: downloadUrl };
        });
        
        const uploadedFiles = await Promise.all(uploadPromises);
        
        // Ensure newUpdateObj.files exists
        if (!newUpdateObj.files) {
          newUpdateObj.files = [];
        }
        
        newUpdateObj.files = [...newUpdateObj.files, ...uploadedFiles];
      }

      await onAddInnovationUpdate(selectedInnovationProject, newUpdateObj);
      
      // Update parent project status if the update has a new status
      if (newUpdateObj.newStatus && selectedInnovationProject) {
        const updatedProject = {
          ...selectedInnovationProject,
          status: newUpdateObj.newStatus,
          idStatus: newUpdateObj.idStatus
        };
        onUpdateInnovationProject(updatedProject);
      }
      
      // If we are editing an existing update, we should also close the view modal
      if (selectedUpdateView) {
        setSelectedUpdateView(null);
        // Cerramos también el visualizador principal para que refresque al volver a abrir
        setIsUpdateViewerModalOpen(false);
      }
      
      // Update local state to reflect the new count immediately
      if (selectedInnovationProject) {
        const fetchUpdates = async () => {
          try {
            const { getInnovationProjectUpdates } = await import('./firebaseInnovationService');
            const currentUpdates = await getInnovationProjectUpdates(selectedInnovationProject);
            const updatedProject = {
              ...selectedInnovationProject,
              updatesCount: String(currentUpdates.length)
            };
            onUpdateInnovationProject(updatedProject);
          } catch (e) {
            console.error('Failed to update count locally', e);
          }
        };
        fetchUpdates();
      }

      setSelectedUpdateFiles([]);
      setIsAddUpdateModalOpen(false);
    } catch (error) {
      console.error("Error saving update with files:", error);
    } finally {
      setIsUploading(false);
    }
  }, [selectedInnovationProject, onAddInnovationUpdate, innovationUpdateForm, selectedUpdateView, selectedUpdateFiles]);

  const isInnovationDetailsOpen = !!selectedInnovationProject;

  const handleDeleteUpdate = useCallback(async () => {
    if (!selectedInnovationProject || !selectedInnovationProject.id || !selectedUpdateView) return;
    
    const updateIdToDelete = selectedUpdateView.id || selectedUpdateView.updateDate;
    
    await onAddInnovationUpdate(selectedInnovationProject, null, true, updateIdToDelete);
    
    setIsUpdateDeleteModalOpen(false);
    setSelectedUpdateView(null);
    setIsUpdateViewerModalOpen(false); // Cerramos para refrescar
  }, [selectedInnovationProject, selectedUpdateView, onAddInnovationUpdate]);

  const [parsedRelatedUpdatesState, setParsedRelatedUpdatesState] = useState<any[]>([]);
  const [, setIsLoadingUpdates] = useState(false);

  useEffect(() => {
    const fetchUpdates = async () => {
      if (selectedInnovationProject && selectedInnovationProject.id) {
        setIsLoadingUpdates(true);
        try {
          const { getInnovationProjectUpdates } = await import('./firebaseInnovationService');
          const updates = await getInnovationProjectUpdates(selectedInnovationProject);
          setParsedRelatedUpdatesState(updates || []);
        } catch (error) {
          console.error("Error fetching updates for project", error);
          setParsedRelatedUpdatesState([]);
        } finally {
          setIsLoadingUpdates(false);
        }
      } else {
        setParsedRelatedUpdatesState([]);
      }
    };
    
    fetchUpdates();
  }, [selectedInnovationProject, innovationDetailsForm?.updatesCount, innovationDetailsForm?.relatedUpdates]);

  const parsedRelatedUpdates = useMemo(() => {
    // Usamos los updates reales descargados de la subcolección
    if (parsedRelatedUpdatesState.length > 0) return parsedRelatedUpdatesState;
    
    // Si tenemos un updatesCount, no deberíamos usar el string antiguo para renderizar todo,
    // pero para mantener compatibilidad con la vista rápida podemos parsear lo que haya quedado
    // o hacer que esta lista venga del padre
    if (!innovationDetailsForm?.relatedUpdates) return [];
    try {
      const parsed = JSON.parse(innovationDetailsForm.relatedUpdates);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      // Legacy text fallback
      return [{
        updateDate: 'Legacy',
        idProjectRef: innovationDetailsForm.project || '',
        newStatus: '',
        pendingDepartment: '',
        plcStage: '',
        developmentProgress: 0,
        technicalNote: innovationDetailsForm.relatedUpdates || '',
        file: '',
        photo: '',
        photoData: '',
      }];
    }
  }, [parsedRelatedUpdatesState, innovationDetailsForm?.relatedUpdates, innovationDetailsForm]);

  const innovationColumnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: '#',
      valueGetter: 'node.rowIndex + 1',
      width: 80,
      minWidth: 80,
      maxWidth: 100,
      pinned: 'left',
      sortable: true,
      sort: 'asc',
      filter: false,
      resizable: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
      showDisabledCheckboxes: true,
      suppressMovable: true,
      cellClass: 'text-gray-500 font-medium',
    },
    {
      headerName: 'ID',
      field: 'id',
      minWidth: 150,
      maxWidth: 180,
      valueGetter: (params: any) => params.data?.id || '',
      cellRenderer: (params: any) => {
        const id = params.value as string;
        if (!id) return null;
        return (
          <span className="text-xs font-mono text-gray-700">{id}</span>
        );
      },
    },
    { headerName: 'Driver', field: 'driver', minWidth: 160, flex: 1 },
    { headerName: 'Topic', field: 'topic', minWidth: 160, flex: 1 },
    { headerName: 'Project', field: 'project', minWidth: 160, flex: 1 },
    { headerName: 'Subproject (Project Title)', field: 'subproject', minWidth: 220, flex: 1.2 },
    {
      headerName: 'Priority',
      field: 'priority',
      minWidth: 120,
      cellClass: (params: any) => {
        const base = 'flex items-center justify-center text-xs font-semibold h-full w-full';
        if (!params.value) return '';
        if (params.value === 'High') return `${base} bg-red-100 text-red-700`;
        if (params.value === 'Medium') return `${base} bg-amber-100 text-amber-700`;
        if (params.value === 'Low') return `${base} bg-gray-100 text-gray-700`;
        return base;
      },
    },
    { headerName: 'Customer', field: 'customer', minWidth: 160, flex: 1 },
    { 
      headerName: 'Status', 
      field: 'status', 
      minWidth: 130,
      cellClass: 'flex items-center justify-center',
      cellRenderer: (params: any) => {
        const status = params.value;
        if (!status) return '';
        
        let colorClass = 'bg-gray-100 text-gray-700 border-gray-200';
        if (status === 'Completed') colorClass = 'bg-green-100 text-green-700 border-green-200';
        if (status === 'In Progress') colorClass = 'bg-amber-100 text-amber-700 border-amber-200';
        if (status === 'On Hold') colorClass = 'bg-gray-100 text-gray-700 border-gray-200';
        if (status === 'Incoming') colorClass = 'bg-purple-100 text-purple-700 border-purple-200';
        if (status === 'Blocked') colorClass = 'bg-red-100 text-red-700 border-red-200';
        
        return (
          <span className={`px-2 py-0.5 text-xs font-medium border rounded-full whitespace-nowrap ${colorClass}`}>
            {status}
          </span>
        );
      }
    },
    {
      headerName: 'Updates',
      field: 'updatesCount',
      minWidth: 100,
      valueGetter: (params: any) => {
        // En Firebase se guarda como string, o puede que estemos pasando a local. 
        // Si no existe pero hay updates cargados localmente en `parsedRelatedUpdatesState` lo comprobamos.
        // Pero idealmente leemos directo de updatesCount.
        const count = params.data?.updatesCount;
        return count !== undefined ? String(count) : '0';
      },
      cellRenderer: (params: any) => {
        const count = params.value;
        return (
          <div className="flex items-center justify-center w-full h-full">
            <span className="text-gray-700 font-semibold">{count}</span>
          </div>
        );
      },
    },
    {
      headerName: 'Lead Technician',
      field: 'leadTechnician',
      minWidth: 220,
      flex: 1.2,
      valueGetter: (params: any) => {
        const email = (params.data?.leadTechnician || '') as string;
        if (!email) return '';
        const localPart = email.split('@')[0] || '';
        const parts = localPart.split(/[.\-_]/).filter(Boolean);
        const initials =
          (parts
            .map((part) => part[0]?.toUpperCase())
            .join('')
            .slice(0, 2) ||
            email[0]?.toUpperCase() ||
            '') + '';
        return `${initials} · ${email}`;
      },
      cellRenderer: (params: any) => {
        const text = (params.value || '') as string;
        if (!text) return '';
        return (
          <span className="block w-full truncate" title={text}>
            {text}
          </span>
        );
      },
      tooltipValueGetter: (params: any) => params.value || '',
      cellStyle: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      cellClass: 'flex items-center h-full text-sm',
    },
    { 
      headerName: 'Country', 
      field: 'country', 
      minWidth: 160,
      maxWidth: 240,
      flex: 0.7,
      valueGetter: (params: any) => countryName || params.data?.country || '',
      cellRenderer: (params: any) => {
        const text = (params.value || '') as string;
        if (!text) return '';
        return (
          <span className="block w-full truncate" title={text}>
            {text}
          </span>
        );
      },
      tooltipValueGetter: (params: any) => params.value || '',
      cellStyle: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      cellClass: 'flex items-center h-full text-sm',
    },
    { 
      headerName: 'Created', 
      field: 'creationDate', 
      minWidth: 130,
      valueFormatter: (params: any) => {
        const dateStr = params.value;
        if (!dateStr || typeof dateStr !== 'string') return '';
        if (dateStr.length === 8) {
          return `${dateStr.slice(6, 8)}/${dateStr.slice(4, 6)}/${dateStr.slice(0, 4)}`;
        }
        return dateStr;
      }
    },
  ], [onSelectInnovationProject, countryName]);

  const innovationDefaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  }), []);

  const onSelectionChanged = useCallback(() => {
    if (gridRef.current) {
      setSelectedRows(gridRef.current.api.getSelectedRows());
    }
  }, []);

  const onGridReady = useCallback(() => { }, []);
  const applyViewMode = useCallback((mode: ViewMode) => {
    if (!gridRef.current) return;
    const api = gridRef.current.api;
    
    // For innovation grid, we ensure we wait for columns to be ready
    setTimeout(() => {
      const allColumnIds = api.getColumns()?.map(col => col.getColId()) || [];
      if (allColumnIds.length === 0) return;

      switch (mode) {
        case 'compact': 
          api.autoSizeColumns(allColumnIds, true); // Shrink to cell content
          break;
        case 'comfortable':
          api.autoSizeColumns(allColumnIds, false); // Fit content + header
          break;
        case 'auto':
          api.sizeColumnsToFit(); // Expand to fill screen horizontally
          break;
      }
    }, 50);
  }, []);
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    applyViewMode(mode);
  }, [applyViewMode]);

  const [] = useState(false);
  const [] = useState<string | null>(null);

  const exportToCSV = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `innovation_projects_${new Date().toISOString()}.csv`,
        columnSeparator: ';',
      });
    }
  }, []);

  const handleExportInnovationJSON = useCallback(() => {
    try {
      if (!innovationData || innovationData.length === 0) {
        setError('No data to export');
        return;
      }
      const dataStr = JSON.stringify({ exportDate: new Date().toISOString(), innovationProjects: innovationData }, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `innovation_projects_${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      setError(error.message || 'Error exporting JSON');
    }
  }, [innovationData, setError]);

  const handleExportInnovationCSV = useCallback(() => {
    try {
      if (!innovationData || innovationData.length === 0) {
        setError('No data to export');
        return;
      }
      
      const headers = Object.keys(innovationData[0]);
      const csvRows = [];
      // Delimitador ';' (no ',') para evitar que comas dentro de textos interfieran en Excel.
      csvRows.push(headers.join(';'));

      for (const row of innovationData) {
        const values = headers.map(header => {
          const val = (row as any)[header];
          const escaped = ('' + (val ?? '')).replace(/"/g, '""');
          return `"${escaped}"`;
        });
        csvRows.push(values.join(';'));
      }
      
      const csvString = csvRows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `innovation_projects_${new Date().toISOString()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      setError(error.message || 'Error exporting CSV');
    }
  }, [innovationData, setError]);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 bg-white border-b border-gray-100">
        <div className="flex items-center gap-4">
          <div className="flex border border-gray-200 rounded-md overflow-hidden shadow-sm">
            {showCommercialActionsButton && (
              <button
                type="button"
                onClick={() => onSectionChange('commercial')}
                className="px-4 py-1.5 text-sm font-semibold transition-colors bg-white text-[#454240] hover:bg-gray-50"
              >
                Commercial Actions
              </button>
            )}
            <button
              type="button"
              className={`px-4 py-1.5 text-sm font-semibold transition-colors bg-[#eb4a4a] text-white ${
                showCommercialActionsButton ? 'border-l border-gray-200' : ''
              }`}
            >
              Innovation Team
            </button>
            {showMitigationButton && (
              <button
                type="button"
                onClick={() => onSectionChange('mitigation')}
                className="px-4 py-1.5 text-sm font-semibold transition-colors border-l border-gray-200 bg-white text-[#454240] hover:bg-gray-50"
              >
                Mitigation
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isInnovationDetailsOpen && (
            <button
              type="button"
              disabled={selectedRows.length !== 1}
              onClick={handleEditSelectedProject}
              className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                selectedRows.length === 1
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Edit className="w-4 h-4" />
              {canEdit ? 'Edit Project' : 'View Project'}
            </button>
          )}

          {!isInnovationDetailsOpen && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">View:</span>
              <div className="flex border border-gray-200 rounded-md overflow-hidden shadow-sm">
                <button onClick={() => handleViewModeChange('compact')} className={`px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === 'compact' ? 'bg-[#eb4a4a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} title="Compact view"><Minimize2 className="w-4 h-4" /></button>
                <button onClick={() => handleViewModeChange('comfortable')} className={`px-3 py-1.5 text-sm font-medium border-x border-gray-200 transition-colors ${viewMode === 'comfortable' ? 'bg-[#eb4a4a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} title="Comfortable view">Comfortable</button>
                <button onClick={() => handleViewModeChange('auto')} className={`px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === 'auto' ? 'bg-[#eb4a4a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} title="Auto view"><Maximize2 className="w-4 h-4" /></button>
              </div>
            </div>
          )}

          {!isInnovationDetailsOpen && (
            <>
              <button onClick={exportToCSV} className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 shadow-sm">Export to CSV</button>

              <button onClick={handleExportInnovationJSON} className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 shadow-sm">
                <Database className="w-4 h-4" />
                Export Data (JSON)
              </button>

              <button onClick={handleExportInnovationCSV} className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 shadow-sm">
                <Database className="w-4 h-4" />
                Export Data (CSV)
              </button>
            </>
          )}

          {isInnovationDetailsOpen && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleBackToList}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50"
              >
                Back to list
              </button>
              <button
                type="button"
                onClick={handleSaveInnovationDetails}
                disabled={!canEdit || !hasUnsavedChanges || isUploading}
                className="px-3 py-1.5 text-sm font-medium text-white bg-[#eb4a4a] rounded-md hover:bg-[#d93a3a] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  canEdit ? 'Save Project' : 'Read Only'
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Grid / Innovation Details */}
      <div className="flex-1 ag-theme-alpine overflow-hidden flex flex-col relative min-h-0">
        {isInnovationDetailsOpen && innovationDetailsForm ? (
          <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-6 absolute inset-0 bg-gray-50/30">
            <div className="w-full space-y-6">
              
              <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-[#454240] flex items-center gap-3">
                  <FileText className="w-6 h-6 text-[#eb4a4a]" />
                  <span>{innovationDetailsForm.subproject || innovationDetailsForm.project || 'Project Details'}</span>
                  {innovationDetailsForm.id && (
                    <span className="text-xs font-mono font-normal text-gray-400 border border-gray-200 rounded-full px-2 py-0.5 bg-gray-50">
                      {innovationDetailsForm.id}
                    </span>
                  )}
                </h2>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 text-xs font-bold rounded-full border ${
                    innovationDetailsForm.priority === 'High' ? 'bg-red-50 text-red-700 border-red-200' :
                    innovationDetailsForm.priority === 'Medium' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                    'bg-gray-50 text-gray-700 border-gray-200'
                  }`}>
                    {innovationDetailsForm.priority} Priority
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column: Main Info */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Definición de Proyecto Card */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                      <Tags className="w-4 h-4 text-gray-500" />
                      <h3 className="font-semibold text-gray-800 text-sm">Project Definition</h3>
                    </div>
                    <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-semibold text-gray-600">Driver</span>
                        <select
                          value={innovationDetailsForm.driver || ''}
                          onChange={(e) => setInnovationDetailsForm(prev => prev ? { ...prev, driver: e.target.value, topic: '', project: '' } : prev)}
                          className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all"
                        >
                          <option value="">Select a driver...</option>
                          {dropdownConfig.drivers.map((driver) => (
                            <option key={driver} value={driver}>{driver}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-semibold text-gray-600">Topic</span>
                        <select
                          value={innovationDetailsForm.topic || ''}
                          onChange={(e) => setInnovationDetailsForm(prev => prev ? { ...prev, topic: e.target.value, project: '' } : prev)}
                          className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all disabled:bg-gray-100 disabled:text-gray-500"
                          disabled={!innovationDetailsForm.driver}
                        >
                          <option value="">
                            {innovationDetailsForm.driver ? 'Select a topic...' : 'Select a driver first'}
                          </option>
                          {innovationDetailsForm.driver && dropdownConfig.topicsByDriver[innovationDetailsForm.driver]?.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-semibold text-gray-600">Project</span>
                        <select
                          value={innovationDetailsForm.project || ''}
                          onChange={(e) => setInnovationDetailsForm(prev => prev ? { ...prev, project: e.target.value } : prev)}
                          className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all disabled:bg-gray-100 disabled:text-gray-500"
                          disabled={!innovationDetailsForm.topic}
                        >
                          <option value="">
                            {innovationDetailsForm.topic ? 'Select a project...' : 'Select a topic first'}
                          </option>
                          {innovationDetailsForm.topic && dropdownConfig.projectsByTopic[innovationDetailsForm.topic]?.map((p) => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-semibold text-gray-600">Subproject (Project Title)</span>
                        <input
                          value={innovationDetailsForm.subproject}
                          onChange={(e) =>
                            setInnovationDetailsForm(prev =>
                              prev ? { ...prev, subproject: e.target.value } : prev
                            )
                          }
                          className="h-10 border border-gray-300 rounded-lg px-3 text-sm focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contexto de Ejecución Card */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                      <Info className="w-4 h-4 text-gray-500" />
                      <h3 className="font-semibold text-gray-800 text-sm">Execution Context</h3>
                    </div>
                    <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-semibold text-gray-600">Context</span>
                        <select
                          value={innovationDetailsForm.context || ''}
                          onChange={(e) =>
                            setInnovationDetailsForm(prev => {
                              if (!prev) return prev;
                              const value = e.target.value;
                              return { ...prev, context: value };
                            })
                          }
                          className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all"
                        >
                          <option value="">Select a context...</option>
                          <option value="R&D">R&D</option>
                          <option value="Beta Tester">Beta Tester</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-semibold text-gray-600">Customer</span>
                        <select
                          value={innovationDetailsForm.customer || ''}
                          onChange={(e) =>
                            setInnovationDetailsForm(prev =>
                              prev ? { ...prev, customer: e.target.value } : prev
                            )
                          }
                          className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all"
                        >
                          <option value="">Select a customer...</option>
                          <option value="No Client">No Client</option>
                          {clientsArray.filter(c => c.id !== 'all').map(client => (
                            <option key={client.id} value={client.name}>
                              {client.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2 flex flex-col gap-1.5">
                        <span className="text-xs font-semibold text-gray-600">Project Description</span>
                        <textarea
                          value={innovationDetailsForm.projectDescription}
                          onChange={(e) => setInnovationDetailsForm(prev => prev ? { ...prev, projectDescription: e.target.value } : prev)}
                          className="h-24 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all"
                          placeholder="Describe the project goals and scope..."
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Tracking & Management */}
                <div className="space-y-6 flex flex-col h-full">
                  {/* Meta Card */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full flex-1">
                    <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2 shrink-0">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <h3 className="font-semibold text-gray-800 text-sm">Tracking & Management</h3>
                    </div>
                    <div className="p-5 flex flex-col gap-4 flex-1">
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-semibold text-gray-600">Lead Technician</span>
                        <input
                          value={innovationDetailsForm.leadTechnician}
                          readOnly
                          className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 text-gray-600 outline-none cursor-not-allowed"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                          <span className="text-xs font-semibold text-gray-600">Priority</span>
                          <select
                            value={innovationDetailsForm.priority}
                            onChange={(e) => setInnovationDetailsForm(prev => prev ? { ...prev, priority: e.target.value as any } : prev)}
                            className={`h-10 border border-gray-300 rounded-lg px-3 text-sm focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all font-medium ${
                              innovationDetailsForm.priority === 'High' ? 'bg-red-50 text-red-700' :
                              innovationDetailsForm.priority === 'Medium' ? 'bg-orange-50 text-orange-700' :
                              'bg-gray-50 text-gray-700'
                            }`}
                          >
                            <option value="High" className="bg-white text-red-700 font-medium">High</option>
                            <option value="Medium" className="bg-white text-orange-700 font-medium">Medium</option>
                            <option value="Low" className="bg-white text-gray-700 font-medium">Low</option>
                          </select>
                        </div>
                        
                        <div className="flex flex-col gap-1.5">
                          <span className="text-xs font-semibold text-gray-600">Status</span>
                          <div className="relative">
                            <input
                              value={innovationDetailsForm.status || 'On Hold'}
                              readOnly
                              className={`h-10 w-full border border-gray-300 rounded-lg px-3 text-sm bg-gray-50 outline-none cursor-not-allowed font-medium truncate pr-1 ${
                                innovationDetailsForm.status === 'Completed' ? 'text-green-700' :
                                innovationDetailsForm.status === 'In Progress' ? 'text-amber-700' :
                                innovationDetailsForm.status === 'Incoming' ? 'text-purple-700' :
                                innovationDetailsForm.status === 'Blocked' ? 'text-red-700' :
                                'text-gray-700'
                              }`}
                              title="Updated via updates"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                          <span className="text-xs font-semibold text-gray-600">Country</span>
                          <input
                            value={innovationDetailsForm.country}
                            readOnly
                            className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 text-gray-600 outline-none cursor-not-allowed"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <span className="text-xs font-semibold text-gray-600">Creation Date</span>
                          <input
                            type="date"
                            value={
                              innovationDetailsForm.creationDate && innovationDetailsForm.creationDate.length === 8
                                ? `${innovationDetailsForm.creationDate.slice(0, 4)}-${innovationDetailsForm.creationDate.slice(4, 6)}-${innovationDetailsForm.creationDate.slice(6, 8)}`
                                : innovationDetailsForm.creationDate || ''
                            }
                            onChange={(e) =>
                              setInnovationDetailsForm(prev =>
                                prev
                                  ? { ...prev, creationDate: e.target.value ? e.target.value.replace(/-/g, '') : '' }
                                  : prev
                              )
                            }
                            className="h-10 border border-gray-300 rounded-lg px-3 text-sm focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all"
                          />
                        </div>
                      </div>
                      
                      {/* Espaciador flexible para empujar Attachments abajo si la tarjeta de la izquierda es más alta */}
                      <div className="flex-1"></div>

                      <div className="flex flex-col gap-1.5 pt-2 border-t border-gray-100 mt-auto">
                        <span className="text-xs font-semibold text-gray-600">Attachments</span>
                        <div className="flex flex-col gap-2">
                          <label className="flex flex-col items-center justify-center gap-2 min-h-[5rem] h-full border-2 border-dashed border-gray-300 rounded-lg px-3 py-4 bg-gray-50 hover:bg-gray-100 hover:border-gray-400 cursor-pointer transition-all text-sm text-gray-600 font-medium group">
                            <UploadCloud className="w-6 h-6 text-gray-400 group-hover:text-emerald-500 transition-colors" />
                            <span>Upload Files</span>
                            <input
                              type="file"
                              multiple
                              className="hidden"
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                if (files.length > 0) {
                                  setSelectedFilesForUpload(prev => [...prev, ...files]);
                                }
                              }}
                            />
                          </label>

                          {/* Summary buttons for multiple files to avoid container height growth */}
                          {selectedFilesForUpload.length > 1 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsFileViewerModalOpen(true);
                              }}
                              className="flex items-center justify-between w-full px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg shadow-sm hover:bg-amber-100 transition-colors text-sm text-amber-700 font-medium"
                            >
                              <div className="flex items-center gap-2">
                                <UploadCloud className="w-4 h-4 text-amber-500" />
                                <span>{selectedFilesForUpload.length} files pending save</span>
                              </div>
                              <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">View / Edit</span>
                            </button>
                          )}

                          {selectedFilesForUpload.length === 1 && (
                            <div className="flex items-center justify-between gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5 shadow-sm w-full mt-1 group/file">
                              <div className="flex items-center gap-2 overflow-hidden">
                                <UploadCloud className="w-4 h-4 text-amber-500 shrink-0" />
                                <span className="truncate font-medium">{selectedFilesForUpload[0].name} (Pending)</span>
                              </div>
                              <button
                                type="button"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedFilesForUpload([]);
                                }}
                                className="text-amber-500 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors cursor-pointer"
                                title="Remove pending file"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                          
                          {innovationDetailsForm.files && innovationDetailsForm.files.length > 1 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsFileViewerModalOpen(true);
                              }}
                              className="flex items-center justify-between w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors text-sm text-gray-700 font-medium"
                            >
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-emerald-600" />
                                <span>{innovationDetailsForm.files.length} saved files</span>
                              </div>
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">View / Edit</span>
                            </button>
                          )}

                          {innovationDetailsForm.files && innovationDetailsForm.files.length === 1 && (
                            <div className="flex items-center justify-between gap-2 text-xs text-gray-600 bg-white border border-gray-200 rounded-md p-2.5 shadow-sm w-full mt-1 group/file">
                              <div className="flex items-center gap-2 overflow-hidden">
                                <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                                <span className="truncate font-medium">{innovationDetailsForm.files[0].name}</span>
                              </div>
                              <div className="flex items-center gap-1 shrink-0 z-10 relative">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.open(innovationDetailsForm.files![0].url, '_blank', 'noopener,noreferrer');
                                  }}
                                  className="text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 p-1 rounded transition-colors cursor-pointer"
                                  title="View file"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  disabled={!canEdit}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!canEdit) return;
                                    setFileToDelete({ url: innovationDetailsForm.files![0].url, idx: 0 });
                                  }}
                                  className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Remove file"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}

                          {innovationDetailsForm.fileName && !innovationDetailsForm.files?.length && (
                            <div className="flex items-center justify-between gap-2 text-xs text-gray-600 bg-white border border-gray-200 rounded-md p-2.5 shadow-sm w-full mt-1 group/file">
                              <div className="flex items-center gap-2 overflow-hidden">
                                <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                                <span className="truncate font-medium">{innovationDetailsForm.fileName}</span>
                              </div>
                              <div className="flex items-center gap-1 shrink-0 z-10 relative">
                                {innovationDetailsForm.fileUrl && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      window.open(innovationDetailsForm.fileUrl, '_blank', 'noopener,noreferrer');
                                    }}
                                    className="text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 p-1 rounded transition-colors cursor-pointer"
                                    title="View file"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  disabled={!canEdit}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!canEdit) return;
                                    if (innovationDetailsForm.fileUrl) {
                                      setFileToDelete({ url: innovationDetailsForm.fileUrl, isLegacy: true });
                                    }
                                  }}
                                  className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Remove file"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Related Updates Grid */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 bg-emerald-50/50 border-b border-emerald-100 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-emerald-800 text-base">Related Updates</h3>
                    <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-md">
                      {parsedRelatedUpdates.length}
                    </span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[1000px]">
                    <thead>
                      <tr className="bg-white border-b border-gray-200 text-xs text-gray-700">
                        <th className="py-3 px-4 font-bold">Update Date</th>
                        <th className="py-3 px-4 font-bold">ID Project Ref</th>
                        <th className="py-3 px-4 font-bold">New Status</th>
                        <th className="py-3 px-4 font-bold">Pending Department</th>
                        <th className="py-3 px-4 font-bold">PLC Stage</th>
                        <th className="py-3 px-4 font-bold">Development Progress</th>
                        <th className="py-3 px-4 font-bold">Technical Note</th>
                        <th className="py-3 px-4 font-bold text-center">File</th>
                        <th className="py-3 px-4 font-bold text-center">Photo</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm text-gray-600 divide-y divide-gray-100">
                      {parsedRelatedUpdates.length > 0 ? (
                        parsedRelatedUpdates.map((upd: any, idx: number) => (
                          <tr 
                            key={upd.id || idx} 
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => setSelectedUpdateView(upd)}
                          >
                            <td className="py-3 px-4 whitespace-nowrap">{formatUpdateDateForDisplay(upd.updateDate)}</td>
                            <td className="py-3 px-4 whitespace-nowrap">{upd.idProjectRef}</td>
                            <td className="py-3 px-4 whitespace-nowrap">{upd.newStatus}</td>
                            <td className="py-3 px-4 whitespace-nowrap">{upd.pendingDepartment}</td>
                            <td className="py-3 px-4 whitespace-nowrap">{upd.plcStage}</td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              {upd.developmentProgress !== undefined ? `${upd.developmentProgress}%` : ''}
                            </td>
                            <td className="py-3 px-4 min-w-[200px] max-w-[300px] truncate">
                              {upd.technicalNote}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap text-center">
                              {upd.files?.length > 0 ? (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md border border-emerald-100">
                                  <FileText className="w-3.5 h-3.5" /> {upd.files.length}
                                </span>
                              ) : upd.file ? (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md border border-emerald-100">
                                  <FileText className="w-3.5 h-3.5" /> 1
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap text-center">
                              {upd.photo ? (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md border border-emerald-100">
                                  <Eye className="w-3.5 h-3.5" /> Yes
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={9} className="py-8 text-center text-gray-500">
                            No updates have been recorded for this project yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Danger Zone */}
              {canEdit && (
                <div className="pt-6 pb-2 flex justify-start">
                  <button
                    type="button"
                    onClick={handleDeleteInnovation}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 hover:text-red-700 focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors shadow-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Project
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <AgGridReact
            ref={gridRef}
            rowData={innovationData}
            columnDefs={innovationColumnDefs}
            defaultColDef={innovationDefaultColDef}
            onGridReady={onGridReady}
            onSelectionChanged={onSelectionChanged}
            animateRows={true}
            rowSelection="single"
            suppressRowClickSelection={false}
            stopEditingWhenCellsLoseFocus={false}
            enableCellTextSelection={true}
            rowHeight={viewMode === 'compact' ? 35 : 42}
            headerHeight={48}
            popupParent={document.body}
          />
        )}
      </div>

      {!isInnovationDetailsOpen && (
        selectedRows.length === 1 ? (
          <button
            onClick={handleEditSelectedProject}
            className="absolute bottom-8 right-8 z-40 flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 text-white rounded-full shadow-xl hover:bg-blue-700 hover:shadow-2xl hover:-translate-y-1 transition-all duration-200 group"
            title={canEdit ? 'Edit Project' : 'View Project'}
          >
            <Edit className="w-6 h-6 transition-transform group-hover:-rotate-6 duration-200" />
            <span className="font-bold tracking-wide">{canEdit ? 'EDIT PROJECT' : 'VIEW PROJECT'}</span>
          </button>
        ) : canEdit ? (
          <button
            onClick={() => setIsAddInnovationModalOpen(true)}
            className="absolute bottom-8 right-8 z-40 flex items-center justify-center gap-2 px-6 py-4 bg-emerald-600 text-white rounded-full shadow-xl hover:bg-emerald-700 hover:shadow-2xl hover:-translate-y-1 transition-all duration-200 group"
            title="Add New Project"
          >
            <Plus className="w-6 h-6 transition-transform group-hover:rotate-90 duration-300" />
            <span className="font-bold tracking-wide">ADD PROJECT</span>
          </button>
        ) : null
      )}

      {isInnovationDetailsOpen && (
        <button
                onClick={() => {
                  // Determine the latest status and progress from related updates
                  let latestStatus = innovationDetailsForm?.status || '';
                  let latestProgress = 0;
                  
                  if (parsedRelatedUpdates && parsedRelatedUpdates.length > 0) {
                    // Sort updates by date descending to find the latest
                    const sortedUpdates = [...parsedRelatedUpdates].sort((a, b) => {
                      const dateA = a.updateDate || '';
                      const dateB = b.updateDate || '';
                      return dateB.localeCompare(dateA);
                    });
                    
                    const latestUpdate = sortedUpdates[0];
                    if (latestUpdate) {
                      latestStatus = latestUpdate.newStatus || latestStatus;
                      latestProgress = latestUpdate.developmentProgress !== undefined ? latestUpdate.developmentProgress : 0;
                    }
                  }

                  setInnovationUpdateForm({
                    updateDate: getCurrentLocalDateTimeInputValue(),
                    updatedByEmail: leadTechnicianEmail,
                    newStatus: latestStatus,
                    pendingDepartment: '',
                    plcStage: '',
                    developmentProgress: latestProgress,
                    technicalNote: '',
                    originalUpdateDate: '', // Clear original date to indicate new update
                  });
                  setIsAddUpdateModalOpen(true);
                }}
          className="absolute bottom-8 right-8 z-40 flex items-center justify-center gap-2 px-6 py-4 bg-emerald-600 text-white rounded-full shadow-xl hover:bg-emerald-700 hover:shadow-2xl hover:-translate-y-1 transition-all duration-200 group"
          title="Add Update"
        >
          <Plus className="w-6 h-6 transition-transform group-hover:rotate-90 duration-300" />
          <span className="font-bold tracking-wide">ADD UPDATE</span>
        </button>
      )}

      {/* Add Innovation Modal */}
      {isAddInnovationModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          onClick={() => setIsAddInnovationModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-100"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#eb4a4a]" />
                Add New Innovation Project
              </h3>
              <button
                onClick={() => setIsAddInnovationModalOpen(false)}
                className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-md transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5 max-h-[70vh] overflow-y-auto">
              
              <div className="md:col-span-2 mt-2">
                <h4 className="text-sm font-bold text-gray-800 border-b pb-2">Project Definition</h4>
              </div>

              {missingCreateFields.length > 0 && (
                <div className="md:col-span-2">
                  <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">Please complete the required fields before creating the project.</div>
                      <div className="mt-0.5">
                        Missing: {missingCreateFields.join(', ')}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700">Driver</span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => openDropdownItemModal('driver', 'create')}
                      className="text-xs font-semibold text-[#eb4a4a] hover:underline"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => openDropdownRenameModal('driver', 'create')}
                      disabled={!innovationForm.driver}
                      className={`text-xs font-semibold ${
                        innovationForm.driver ? 'text-gray-600 hover:underline' : 'text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      Edit
                    </button>
                  </div>
                </div>
                <select
                  autoFocus
                  value={innovationForm.driver}
                  onChange={(e) => setInnovationForm((prev) => ({ ...prev, driver: e.target.value, topic: '', project: '' }))}
                  className={`h-10 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all shadow-sm ${
                    missingCreateFields.includes('Driver') ? 'border border-red-400' : 'border border-gray-300'
                  }`}
                >
                  <option value="">Select a driver...</option>
                  {dropdownConfig.drivers.map((driver) => (
                    <option key={driver} value={driver}>{driver}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700">
                    Topic <span className="text-red-500">*</span>
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => openDropdownItemModal('topic', 'create')}
                      disabled={!innovationForm.driver}
                      className={`text-xs font-semibold ${
                        innovationForm.driver ? 'text-[#eb4a4a] hover:underline' : 'text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => openDropdownRenameModal('topic', 'create')}
                      disabled={!innovationForm.driver || !innovationForm.topic}
                      className={`text-xs font-semibold ${
                        innovationForm.driver && innovationForm.topic
                          ? 'text-gray-600 hover:underline'
                          : 'text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      Edit
                    </button>
                  </div>
                </div>
                <select
                  value={innovationForm.topic}
                  onChange={(e) => setInnovationForm((prev) => ({ ...prev, topic: e.target.value, project: '' }))}
                  className={`h-10 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all shadow-sm disabled:bg-gray-100 disabled:text-gray-500 ${
                    missingCreateFields.includes('Topic') ? 'border border-red-400' : 'border border-gray-300'
                  }`}
                  disabled={!innovationForm.driver}
                >
                  <option value="">
                    {innovationForm.driver ? 'Select a topic...' : 'Select a driver first'}
                  </option>
                  {innovationForm.driver && dropdownConfig.topicsByDriver[innovationForm.driver]?.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700">
                    Project <span className="text-red-500">*</span>
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => openDropdownItemModal('project', 'create')}
                      disabled={!innovationForm.topic}
                      className={`text-xs font-semibold ${
                        innovationForm.topic ? 'text-[#eb4a4a] hover:underline' : 'text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => openDropdownRenameModal('project', 'create')}
                      disabled={!innovationForm.topic || !innovationForm.project}
                      className={`text-xs font-semibold ${
                        innovationForm.topic && innovationForm.project
                          ? 'text-gray-600 hover:underline'
                          : 'text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      Edit
                    </button>
                  </div>
                </div>
                <select
                  value={innovationForm.project}
                  onChange={(e) => setInnovationForm((prev) => ({ ...prev, project: e.target.value }))}
                  className={`h-10 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all shadow-sm disabled:bg-gray-100 disabled:text-gray-500 ${
                    missingCreateFields.includes('Project') ? 'border border-red-400' : 'border border-gray-300'
                  }`}
                  disabled={!innovationForm.topic}
                >
                  <option value="">
                    {innovationForm.topic ? 'Select a project...' : 'Select a topic first'}
                  </option>
                  {innovationForm.topic && dropdownConfig.projectsByTopic[innovationForm.topic]?.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">Subproject (Project Title)</span>
                <input
                  value={innovationForm.subproject}
                  onChange={(e) => setInnovationForm((prev) => ({ ...prev, subproject: e.target.value }))}
                  className={`h-10 rounded-lg px-3 text-sm focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all shadow-sm ${
                    missingCreateFields.includes('Subproject') ? 'border border-red-400' : 'border border-gray-300'
                  }`}
                />
              </div>

              <div className="md:col-span-2 mt-4">
                <h4 className="text-sm font-bold text-gray-800 border-b pb-2">Execution Context</h4>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">Context</span>
                <select
                  value={innovationForm.context}
                  onChange={(e) =>
                    setInnovationForm(prev => {
                      const value = e.target.value;
                      return { ...prev, context: value };
                    })
                  }
                  className={`h-10 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all shadow-sm ${
                    missingCreateFields.includes('Context') ? 'border border-red-400' : 'border border-gray-300'
                  }`}
                >
                  <option value="">Select a context...</option>
                  <option value="R&D">R&D</option>
                  <option value="Beta Tester">Beta Tester</option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">Customer</span>
                <select
                  value={innovationForm.customer || ''}
                  onChange={(e) =>
                    setInnovationForm(prev => ({ ...prev, customer: e.target.value }))
                  }
                  className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all shadow-sm"
                >
                  <option value="">Select a customer...</option>
                  <option value="No Client">No Client</option>
                  {clientsArray.filter(c => c.id !== 'all').map(client => (
                    <option key={client.id} value={client.name}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="md:col-span-2 flex flex-col gap-1.5 pt-2">
                <span className="text-xs font-semibold text-gray-700">Project Description</span>
                <textarea
                  value={innovationForm.projectDescription}
                  onChange={(e) => setInnovationForm((prev) => ({ ...prev, projectDescription: e.target.value }))}
                  className="h-24 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all shadow-sm"
                  placeholder="Provide details about the project goals and context..."
                />
              </div>

              <div className="md:col-span-2 mt-4">
                <h4 className="text-sm font-bold text-gray-800 border-b pb-2">Tracking & Management</h4>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">Priority</span>
                <select
                  value={innovationForm.priority}
                  onChange={(e) =>
                    setInnovationForm(prev => ({ ...prev, priority: e.target.value as any }))
                  }
                  className={`h-10 border border-gray-300 rounded-lg px-3 text-sm focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all shadow-sm font-medium ${
                    innovationForm.priority === 'High'
                      ? 'bg-red-50 text-red-700'
                      : innovationForm.priority === 'Medium'
                      ? 'bg-orange-50 text-orange-700'
                      : 'bg-gray-50 text-gray-700'
                  }`}
                >
                  <option value="High" className="bg-white text-red-700 font-medium">
                    High
                  </option>
                  <option value="Medium" className="bg-white text-orange-700 font-medium">
                    Medium
                  </option>
                  <option value="Low" className="bg-white text-gray-700 font-medium">
                    Low
                  </option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">Lead Technician</span>
                <input
                  value={innovationForm.leadTechnician}
                  onChange={(e) => setInnovationForm((prev) => ({ ...prev, leadTechnician: e.target.value }))}
                  className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all shadow-sm"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-semibold text-gray-700">Country</span>
                        <select
                          value={innovationForm.country || ''}
                          onChange={(e) =>
                            setInnovationForm(prev => ({ ...prev, country: e.target.value }))
                          }
                          className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all shadow-sm"
                        >
                          <option value="">Auto-generated based on filter</option>
                          <option value="GLOBAL">GLOBAL</option>
                          <option value="spain">Spain (ES)</option>
                          <option value="italy">Italy</option>
                          <option value="brazil">Brazil</option>
                          <option value="india">India</option>
                          <option value="china">China</option>
                          <option value="mexico">Mexico</option>
                          <option value="usa">USA</option>
                          <option value="portugal">Portugal</option>
                          <option value="fritta-spain">Spain (FR)</option>
                          <option value="younexa-spain">Spain (YN)</option>
                          <option value="turkey">Turkey</option>
                        </select>
                      </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">Creation Date</span>
                <input
                  type="date"
                  value={innovationForm.creationDate}
                  onChange={(e) => setInnovationForm((prev) => ({ ...prev, creationDate: e.target.value }))}
                  className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all shadow-sm"
                />
              </div>

              <div className="md:col-span-2 flex flex-col gap-1.5 pt-4 pb-2 border-t border-gray-100">
                <span className="text-xs font-semibold text-gray-700">Attachments</span>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center justify-center gap-2 h-10 border border-dashed border-gray-300 rounded-lg px-3 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors text-sm text-gray-600 font-medium">
                    <UploadCloud className="w-4 h-4" />
                    <span>Upload Files</span>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length > 0) {
                          setSelectedFilesForUpload(prev => [...prev, ...files]);
                        }
                      }}
                    />
                  </label>

                  {/* Summary buttons for multiple files to avoid container height growth */}
                  {selectedFilesForUpload.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsFileViewerModalOpen(true);
                      }}
                      className="flex items-center justify-between w-full px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg shadow-sm hover:bg-amber-100 transition-colors text-sm text-amber-700 font-medium"
                    >
                      <div className="flex items-center gap-2">
                        <UploadCloud className="w-4 h-4 text-amber-500" />
                        <span>{selectedFilesForUpload.length} files pending save</span>
                      </div>
                      <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">View / Edit</span>
                    </button>
                  )}

                  {selectedFilesForUpload.length === 1 && (
                    <div className="flex items-center justify-between gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5 shadow-sm w-full mt-1 group/file">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <UploadCloud className="w-4 h-4 text-amber-500 shrink-0" />
                        <span className="truncate font-medium">{selectedFilesForUpload[0].name} (Pending)</span>
                      </div>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedFilesForUpload([]);
                        }}
                        className="text-amber-500 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors cursor-pointer"
                        title="Remove pending file"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => setIsAddInnovationModalOpen(false)}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#eb4a4a] transition-all shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAddInnovationProject}
                disabled={isUploading}
                className="px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create Project
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Viewer/Manager Modal */}
      {isFileViewerModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          onClick={() => setIsFileViewerModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50 shrink-0">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Manage Attached Files
              </h3>
              <button
                onClick={() => setIsFileViewerModalOpen(false)}
                className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-md transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-6">
              {/* Already Saved Files Section */}
              {((innovationDetailsForm?.files && innovationDetailsForm.files.length > 0) || (innovationDetailsForm?.fileName && !innovationDetailsForm?.files?.length)) && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-2">Saved Files</h4>
                  <div className="flex flex-col gap-2">
                    {innovationDetailsForm?.files?.map((file, idx) => (
                      <div key={`modal-uploaded-${idx}`} className="flex items-center justify-between gap-3 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:border-emerald-300 transition-colors group">
                        <div className="flex items-center gap-3 overflow-hidden">
                          <div className="p-2 bg-emerald-50 rounded-lg shrink-0">
                            <FileText className="w-5 h-5 text-emerald-600" />
                          </div>
                          <span className="truncate font-medium" title={file.name}>{file.name}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.open(file.url, '_blank', 'noopener,noreferrer');
                            }}
                            className="text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 p-2 rounded-md transition-colors"
                            title="View file"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              try {
                                const blob = file.url.includes('firebasestorage')
                                  ? await downloadInnovationProjectFileBlob(file.url)
                                  : await (await fetch(file.url)).blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.style.display = 'none';
                                a.href = url;
                                a.download = file.name || 'download';
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                              } catch (error) {
                                window.open(file.url, '_blank', 'noopener,noreferrer');
                              }
                            }}
                            className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 p-2 rounded-md transition-colors"
                            title="Download file"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            disabled={!canEdit}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!canEdit) return;
                              setFileToDelete({ url: file.url, idx });
                            }}
                            className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete file"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}

                    {innovationDetailsForm?.fileName && !innovationDetailsForm?.files?.length && (
                      <div className="flex items-center justify-between gap-3 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:border-emerald-300 transition-colors group">
                        <div className="flex items-center gap-3 overflow-hidden">
                          <div className="p-2 bg-emerald-50 rounded-lg shrink-0">
                            <FileText className="w-5 h-5 text-emerald-600" />
                          </div>
                          <span className="truncate font-medium" title={innovationDetailsForm.fileName}>{innovationDetailsForm.fileName}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {innovationDetailsForm.fileUrl && (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  window.open(innovationDetailsForm.fileUrl, '_blank', 'noopener,noreferrer');
                                }}
                                className="text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 p-2 rounded-md transition-colors"
                                title="View file"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  try {
                                    const fileUrl = innovationDetailsForm.fileUrl as string;
                                    const blob = fileUrl.includes('firebasestorage')
                                      ? await downloadInnovationProjectFileBlob(fileUrl)
                                      : await (await fetch(fileUrl)).blob();
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.style.display = 'none';
                                    a.href = url;
                                    a.download = innovationDetailsForm.fileName || 'download';
                                    document.body.appendChild(a);
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                    document.body.removeChild(a);
                                  } catch (error) {
                                    window.open(innovationDetailsForm.fileUrl, '_blank', 'noopener,noreferrer');
                                  }
                                }}
                                className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 p-2 rounded-md transition-colors"
                                title="Download file"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            disabled={!canEdit}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!canEdit) return;
                              if (innovationDetailsForm.fileUrl) {
                                setFileToDelete({ url: innovationDetailsForm.fileUrl, isLegacy: true });
                              }
                            }}
                            className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete file"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pending Files Section */}
              {selectedFilesForUpload.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-amber-800 border-b border-amber-100 pb-2">Pending Upload (Will be saved when you click Save Project)</h4>
                  <div className="flex flex-col gap-2">
                    {selectedFilesForUpload.map((file, idx) => (
                      <div key={`modal-pending-${idx}`} className="flex items-center justify-between gap-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 shadow-sm">
                        <div className="flex items-center gap-3 overflow-hidden">
                          <div className="p-2 bg-amber-100 rounded-lg shrink-0">
                            <UploadCloud className="w-5 h-5 text-amber-600" />
                          </div>
                          <div className="flex flex-col">
                            <span className="truncate font-medium" title={file.name}>{file.name}</span>
                            <span className="text-xs text-amber-600/80">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedFilesForUpload(prev => prev.filter((_, i) => i !== idx));
                          }}
                          className="text-amber-500 hover:text-red-600 hover:bg-red-50 p-2 rounded-md transition-colors shrink-0"
                          title="Remove pending file"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedFilesForUpload.length === 0 && (!innovationDetailsForm?.files?.length && !innovationDetailsForm?.fileName) && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No files attached to this project.</p>
                </div>
              )}
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end shrink-0">
              <button
                onClick={() => setIsFileViewerModalOpen(false)}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Update Modal */}
      {isUpdateViewerModalOpen && updateViewerList.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          onClick={() => setIsUpdateViewerModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden border border-gray-100 flex flex-col max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50 shrink-0">
              <div className="flex flex-col">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Project Updates
                </h3>
                <p className="text-xs text-gray-500 mt-1 font-medium">
                  {updateViewerProject?.project} {updateViewerProject?.subproject ? `- ${updateViewerProject.subproject}` : ''}
                </p>
              </div>
              <button
                onClick={() => setIsUpdateViewerModalOpen(false)}
                className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-md transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {updateViewerList.length > 1 && (
              <div className="p-4 border-b border-gray-100 bg-gray-50/30 shrink-0">
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Select an update to view:</label>
                <select
                  value={updateViewerSelectedId}
                  onChange={(e) => setUpdateViewerSelectedId(e.target.value)}
                  className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all shadow-sm font-medium"
                >
                  {updateViewerList.map((upd, idx) => {
                    // Usamos un index como fallback en la option para asegurarnos de que la clave/value es única incluso si updateDate es igual
                    const uniqueId = `${upd.updateDate}-${idx}`;
                    return (
                      <option key={uniqueId} value={uniqueId}>
                        {formatUpdateDateForDisplay(upd.updateDate)} {idx === 0 ? '(Latest)' : ''} - Status: {upd.newStatus || 'No status'}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            <div className="p-6 space-y-5 overflow-y-auto text-sm bg-white flex-1">
              {updateViewerList
                .map((upd, idx) => ({ ...upd, _uniqueId: `${upd.updateDate}-${idx}` }))
                .filter(u => u._uniqueId === updateViewerSelectedId)
                .map((selectedUpdate) => (
                <div key={selectedUpdate.updateDate} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">Update Date</p>
                      <p className="mt-1 text-gray-900 font-medium bg-gray-50 px-3 py-1.5 rounded-md border border-gray-100 inline-block">{formatUpdateDateForDisplay(selectedUpdate.updateDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">ID Project Ref</p>
                      <p className="mt-1 text-gray-900 font-medium">{selectedUpdate.idProjectRef}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">New Status</p>
                      <p className="mt-1 text-gray-900 font-medium flex items-center gap-2">
                        {selectedUpdate.newStatus ? (
                          <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-bold">{selectedUpdate.newStatus}</span>
                        ) : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">Pending Department</p>
                      <p className="mt-1 text-gray-900 font-medium">{selectedUpdate.pendingDepartment || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">PLC Stage</p>
                      <p className="mt-1 text-gray-900 font-medium">{selectedUpdate.plcStage || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">Development Progress</p>
                      {selectedUpdate.developmentProgress !== undefined ? (
                        <div className="mt-2 flex items-center gap-3">
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-emerald-500 rounded-full" 
                              style={{ width: `${selectedUpdate.developmentProgress}%` }}
                            />
                          </div>
                          <span className="font-bold text-gray-700 w-9">{selectedUpdate.developmentProgress}%</span>
                        </div>
                      ) : (
                        <p className="mt-1 text-gray-900 font-medium">-</p>
                      )}
                    </div>
                  </div>

                  <div className="pt-5 mt-5 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase">Technical Note</p>
                    <div className="mt-2 p-4 bg-[#f8fafc] border border-gray-100 rounded-xl text-gray-700 whitespace-pre-wrap min-h-[100px] break-words shadow-sm leading-relaxed">
                      {selectedUpdate.technicalNote || <span className="text-gray-400 italic">No technical notes provided.</span>}
                    </div>
                  </div>

                  <div className="pt-5 mt-5 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Attached Files</p>
                    
                    {selectedUpdate.files && selectedUpdate.files.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        {selectedUpdate.files.map((file: any, idx: number) => (
                          <div key={`viewer-file-${idx}`} className="flex items-center justify-between gap-3 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:border-emerald-300 transition-colors group">
                            <div className="flex items-center gap-3 overflow-hidden">
                              <div className="p-2 bg-emerald-50 rounded-lg shrink-0">
                                <FileText className="w-5 h-5 text-emerald-600" />
                              </div>
                              <span className="truncate font-medium" title={file.name}>{file.name}</span>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  window.open(file.url, '_blank', 'noopener,noreferrer');
                                }}
                                className="text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 p-2 rounded-md transition-colors"
                                title="View file"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  try {
                                    const a = document.createElement('a');
                                    a.href = file.url;
                                    a.download = file.name;
                                    a.target = '_blank';
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                  } catch (error) {
                                    window.open(file.url, '_blank', 'noopener,noreferrer');
                                  }
                                }}
                                className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 p-2 rounded-md transition-colors"
                                title="Download file"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-xs italic bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 w-fit">No files attached</p>
                    )}
                  </div>

                  {/* Legacy support in viewer */}
                  {(selectedUpdate.file || selectedUpdate.photo) && (
                    <div className="pt-5 mt-5 border-t border-gray-100 grid grid-cols-2 gap-6">
                      {selectedUpdate.file && (
                        <div className="flex flex-col">
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Legacy File</p>
                          <div className="flex items-center gap-2 p-3 bg-white text-emerald-700 rounded-lg border border-gray-200 shadow-sm w-fit hover:border-emerald-300 transition-colors">
                            <FileText className="w-4 h-4 shrink-0" />
                            <span className="truncate text-xs font-medium max-w-[200px]">{selectedUpdate.file}</span>
                          </div>
                        </div>
                      )}

                      {selectedUpdate.photo && (
                        <div className="flex flex-col">
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Legacy Photo</p>
                          <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-2 p-3 bg-white text-emerald-700 rounded-lg border border-gray-200 shadow-sm w-fit hover:border-emerald-300 transition-colors">
                              <Eye className="w-4 h-4 shrink-0" />
                              <span className="truncate text-xs font-medium max-w-[200px]">{selectedUpdate.photo}</span>
                            </div>
                            {selectedUpdate.photoData && (
                              <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm inline-block max-w-full">
                                <img 
                                  src={selectedUpdate.photoData} 
                                  alt="Attached" 
                                  className="w-auto h-auto max-h-[300px] max-w-full object-contain bg-gray-50"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end shrink-0">
              <button
                onClick={() => setIsUpdateViewerModalOpen(false)}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Update Modal */}
      {isAddUpdateModalOpen && innovationDetailsForm && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setIsAddUpdateModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden border border-gray-100"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-600" />
                Add Project Update
              </h3>
              <button
                onClick={() => setIsAddUpdateModalOpen(false)}
                className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-md transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5 max-h-[70vh] overflow-y-auto text-sm">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">ID Project Ref</span>
                <input
                  value={innovationDetailsForm.id || ''}
                  readOnly
                  className="h-10 border border-gray-300 rounded-lg px-3 bg-gray-100 text-gray-600 outline-none cursor-not-allowed"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">Update Date</span>
                <input
                  type="datetime-local"
                  value={innovationUpdateForm.updateDate}
                  onChange={(e) =>
                    setInnovationUpdateForm(prev => ({ ...prev, updateDate: e.target.value }))
                  }
                  className="h-10 border border-gray-300 rounded-lg px-3 bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">Updated By</span>
                <input
                  value={innovationUpdateForm.updatedByEmail}
                  onChange={(e) =>
                    setInnovationUpdateForm(prev => ({ ...prev, updatedByEmail: e.target.value }))
                  }
                  className="h-10 border border-gray-300 rounded-lg px-3 bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">New Status</span>
                <select
                  value={innovationUpdateForm.newStatus}
                  onChange={(e) =>
                    setInnovationUpdateForm(prev => ({ ...prev, newStatus: e.target.value }))
                  }
                  className="h-10 border border-gray-300 rounded-lg px-3 bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all"
                >
                  <option value="">Select...</option>
                  {updateDropdownConfig.statuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">Pending Department</span>
                <select
                  value={innovationUpdateForm.pendingDepartment}
                  onChange={(e) =>
                    setInnovationUpdateForm(prev => ({ ...prev, pendingDepartment: e.target.value }))
                  }
                  className="h-10 border border-gray-300 rounded-lg px-3 bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all"
                >
                  <option value="" disabled hidden>Select department...</option>
                  {updateDropdownConfig.departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">PLC Stage</span>
                <select
                  value={innovationUpdateForm.plcStage}
                  onChange={(e) =>
                    setInnovationUpdateForm(prev => ({ ...prev, plcStage: e.target.value }))
                  }
                  className="h-10 border border-gray-300 rounded-lg px-3 bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all"
                >
                  <option value="" disabled hidden>Select PLC Stage...</option>
                  {updateDropdownConfig.plcStages.map((stage) => (
                    <option key={stage} value={stage}>
                      {stage}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">Development Progress (%)</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-600 min-w-[3rem]">{innovationUpdateForm.developmentProgress || 0}%</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="10"
                    value={innovationUpdateForm.developmentProgress}
                    onChange={(e) =>
                      setInnovationUpdateForm(prev => ({
                        ...prev,
                        developmentProgress: Number(e.target.value),
                      }))
                    }
                    style={{
                      background: `linear-gradient(to right, #10b981 0%, #10b981 ${innovationUpdateForm.developmentProgress || 0}%, #e5e7eb ${innovationUpdateForm.developmentProgress || 0}%, #e5e7eb 100%)`,
                    }}
                    className="flex-1 h-2 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                  />
                </div>
              </div>
              <div className="md:col-span-2 flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">Technical Note</span>
                <textarea
                  value={innovationUpdateForm.technicalNote}
                  onChange={(e) =>
                    setInnovationUpdateForm(prev => ({ ...prev, technicalNote: e.target.value }))
                  }
                  className="min-h-[100px] border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all"
                  placeholder="Describe the main learning, issues or decisions taken..."
                />
              </div>
              <div className="md:col-span-2 flex flex-col gap-1.5 pt-4 pb-2 border-t border-gray-100">
                <span className="text-xs font-semibold text-gray-700">Attachments</span>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center justify-center gap-2 h-10 border border-dashed rounded-lg px-3 cursor-pointer transition-colors text-sm font-medium border-gray-300 bg-gray-50 text-gray-600 hover:bg-gray-100">
                    <UploadCloud className="w-4 h-4" />
                    <span>Upload Files / Photos</span>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length > 0) {
                          setSelectedUpdateFiles(prev => [...prev, ...files]);
                        }
                      }}
                    />
                  </label>

                  {/* Pending Update Files */}
                  {selectedUpdateFiles.map((file, idx) => (
                    <div key={`update-new-${idx}`} className="flex items-center justify-between gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5 shadow-sm w-full mt-1">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <UploadCloud className="w-4 h-4 text-amber-500 shrink-0" />
                        <span className="truncate font-medium">{file.name} (Pending)</span>
                      </div>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedUpdateFiles(prev => prev.filter((_, i) => i !== idx));
                        }}
                        className="text-amber-500 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors cursor-pointer"
                        title="Remove file"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  {/* Legacy File support */}
                  {selectedUpdateView?.file && (
                    <div className="flex items-center justify-between gap-2 p-2 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-100 shadow-sm mt-1">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <FileText className="w-4 h-4 shrink-0" />
                        <span className="truncate text-xs font-medium">{selectedUpdateView.file} (Legacy)</span>
                      </div>
                    </div>
                  )}

                  {/* Legacy Photo support */}
                  {selectedUpdateView?.photo && (
                    <div className="flex items-center justify-between gap-2 p-2 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-100 shadow-sm mt-1">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <Eye className="w-4 h-4 shrink-0" />
                        <span className="truncate text-xs font-medium">{selectedUpdateView.photo} (Legacy)</span>
                      </div>
                    </div>
                  )}

                  {/* Saved Update Files */}
                  {selectedUpdateView?.files?.map((file: any, idx: number) => (
                    <div key={`update-saved-${idx}`} className="flex items-center justify-between gap-2 text-xs text-gray-600 bg-white border border-gray-200 rounded-md p-2.5 shadow-sm w-full mt-1">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span className="truncate font-medium">{file.name}</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(file.url, '_blank');
                          }}
                          className="p-1.5 text-gray-400 hover:text-emerald-600 rounded-md transition-colors"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            // Trigger confirmation modal for update file deletion
                            setFileToDelete({
                              url: file.url,
                              idx: idx,
                              isLegacy: false,
                              isUpdate: true // We need to flag this to handle it correctly
                            });
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete file"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => {
                  setIsAddUpdateModalOpen(false);
                  setSelectedUpdateFiles([]);
                }}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveInnovationUpdate}
                disabled={isUploading}
                className="px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Save Update
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDropdownItemModalOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          onClick={closeDropdownItemModal}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-100"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Database className="w-5 h-5 text-[#eb4a4a]" />
                {dropdownItemModalMode === 'driver'
                  ? 'Add Driver'
                  : dropdownItemModalMode === 'topic'
                  ? 'Add Topic'
                  : 'Add Project'}
              </h3>
              <button
                type="button"
                onClick={closeDropdownItemModal}
                className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-md transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-sm">
              {dropdownItemModalMode !== 'driver' && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                  {dropdownItemModalMode === 'topic' ? (
                    <>
                      <span className="font-semibold">Driver:</span>{' '}
                      {dropdownItemModalTarget === 'create'
                        ? innovationForm.driver || '(none)'
                        : innovationDetailsForm?.driver || '(none)'}
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">Topic:</span>{' '}
                      {dropdownItemModalTarget === 'create'
                        ? innovationForm.topic || '(none)'
                        : innovationDetailsForm?.topic || '(none)'}
                    </>
                  )}
                </div>
              )}

              {dropdownItemModalMode === 'driver' && (
                <div className="space-y-3">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-gray-700">Driver name *</span>
                    <input
                      value={dropdownItemModalState.driverName}
                      onChange={(e) =>
                        setDropdownItemModalState((prev) => ({ ...prev, driverName: e.target.value }))
                      }
                      className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all"
                      placeholder="e.g. Surface Aesthetics"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-700">Topics *</span>
                      <button
                        type="button"
                        onClick={addTopicTreeRow}
                        className="text-xs font-semibold text-[#eb4a4a] hover:underline"
                      >
                        + Add Topic
                      </button>
                    </div>
                    <div className="space-y-3">
                      {dropdownItemModalState.topicTrees.map((node, topicIndex) => (
                        <div key={`topic-tree-${topicIndex}`} className="rounded-lg border border-gray-200 p-3 bg-white">
                          <div className="flex items-center gap-2">
                            <input
                              value={node.topic}
                              onChange={(e) => updateTopicTreeName(topicIndex, e.target.value)}
                              className="h-10 flex-1 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all"
                              placeholder="Topic name"
                            />
                            {dropdownItemModalState.topicTrees.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeTopicTreeRow(topicIndex)}
                                className="h-10 w-10 border border-gray-300 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-50"
                                aria-label="Remove topic"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          <div className="mt-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-gray-700">Projects *</span>
                              <button
                                type="button"
                                onClick={() => addTopicTreeProjectRow(topicIndex)}
                                className="text-xs font-semibold text-[#eb4a4a] hover:underline"
                              >
                                + Add Project
                              </button>
                            </div>
                            <div className="space-y-2">
                              {node.projects.map((projectValue, projectIndex) => (
                                <div key={`topic-${topicIndex}-project-${projectIndex}`} className="flex items-center gap-2">
                                  <input
                                    value={projectValue}
                                    onChange={(e) =>
                                      updateTopicTreeProjectName(topicIndex, projectIndex, e.target.value)
                                    }
                                    className="h-10 flex-1 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all"
                                    placeholder="Project name"
                                  />
                                  {node.projects.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => removeTopicTreeProjectRow(topicIndex, projectIndex)}
                                      className="h-10 w-10 border border-gray-300 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-50"
                                      aria-label="Remove project"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {dropdownItemModalMode === 'topic' && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-700">Topics *</span>
                      <button
                        type="button"
                        onClick={addTopicTreeRow}
                        className="text-xs font-semibold text-[#eb4a4a] hover:underline"
                      >
                        + Add Topic
                      </button>
                    </div>
                    <div className="space-y-3">
                      {dropdownItemModalState.topicTrees.map((node, topicIndex) => (
                        <div key={`topic-tree-${topicIndex}`} className="rounded-lg border border-gray-200 p-3 bg-white">
                          <div className="flex items-center gap-2">
                            <input
                              value={node.topic}
                              onChange={(e) => updateTopicTreeName(topicIndex, e.target.value)}
                              className="h-10 flex-1 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all"
                              placeholder="Topic name"
                            />
                            {dropdownItemModalState.topicTrees.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeTopicTreeRow(topicIndex)}
                                className="h-10 w-10 border border-gray-300 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-50"
                                aria-label="Remove topic"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          <div className="mt-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-gray-700">Projects *</span>
                              <button
                                type="button"
                                onClick={() => addTopicTreeProjectRow(topicIndex)}
                                className="text-xs font-semibold text-[#eb4a4a] hover:underline"
                              >
                                + Add Project
                              </button>
                            </div>
                            <div className="space-y-2">
                              {node.projects.map((projectValue, projectIndex) => (
                                <div key={`topic-${topicIndex}-project-${projectIndex}`} className="flex items-center gap-2">
                                  <input
                                    value={projectValue}
                                    onChange={(e) =>
                                      updateTopicTreeProjectName(topicIndex, projectIndex, e.target.value)
                                    }
                                    className="h-10 flex-1 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all"
                                    placeholder="Project name"
                                  />
                                  {node.projects.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => removeTopicTreeProjectRow(topicIndex, projectIndex)}
                                      className="h-10 w-10 border border-gray-300 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-50"
                                      aria-label="Remove project"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {dropdownItemModalMode === 'project' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700">Projects *</span>
                    <button
                      type="button"
                      onClick={addProjectOptionRow}
                      className="text-xs font-semibold text-[#eb4a4a] hover:underline"
                    >
                      + Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {dropdownItemModalState.projectOptions.map((value, index) => (
                      <div key={`project-${index}`} className="flex items-center gap-2">
                        <input
                          value={value}
                          onChange={(e) => updateProjectOption(index, e.target.value)}
                          className="h-10 flex-1 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all"
                          placeholder="Project name"
                        />
                        {dropdownItemModalState.projectOptions.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeProjectOptionRow(index)}
                            className="h-10 w-10 border border-gray-300 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-50"
                            aria-label="Remove project"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-4 bg-gray-50 border-t">
              <button
                type="button"
                onClick={closeDropdownItemModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#eb4a4a]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateDropdownItem}
                className="px-4 py-2 text-sm font-medium text-white bg-[#eb4a4a] rounded-md hover:bg-[#d93a3a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {isDropdownRenameModalOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          onClick={closeDropdownRenameModal}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-100"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Edit className="w-5 h-5 text-[#454240]" />
                {dropdownRenameModalMode === 'driver'
                  ? 'Edit Driver'
                  : dropdownRenameModalMode === 'topic'
                  ? 'Edit Topic'
                  : 'Edit Project'}
              </h3>
              <button
                type="button"
                onClick={closeDropdownRenameModal}
                className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-md transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-sm">
              {dropdownRenameModalMode !== 'driver' && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                  {dropdownRenameModalMode === 'topic' ? (
                    <>
                      <span className="font-semibold">Driver:</span>{' '}
                      {dropdownRenameModalTarget === 'create'
                        ? innovationForm.driver || '(none)'
                        : innovationDetailsForm?.driver || '(none)'}
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">Topic:</span>{' '}
                      {dropdownRenameModalTarget === 'create'
                        ? innovationForm.topic || '(none)'
                        : innovationDetailsForm?.topic || '(none)'}
                    </>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">Current name</span>
                <input
                  value={dropdownRenameModalOldName}
                  readOnly
                  className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 text-gray-600 outline-none cursor-not-allowed"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-700">New name *</span>
                <input
                  autoFocus
                  value={dropdownRenameModalNewName}
                  onChange={(e) => setDropdownRenameModalNewName(e.target.value)}
                  className="h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:ring-2 focus:ring-[#eb4a4a]/20 focus:border-[#eb4a4a] outline-none transition-all"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 bg-gray-50 border-t">
              <button
                type="button"
                onClick={closeDropdownRenameModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#eb4a4a]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRenameDropdownItem}
                className="px-4 py-2 text-sm font-medium text-white bg-[#eb4a4a] rounded-md hover:bg-[#d93a3a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Innovation Confirmation Modal */}
      {isInnovationDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#eb4a4a]" />
                Confirm Deletion
              </h3>
              <button
                onClick={() => setIsInnovationDeleteModalOpen(false)}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600">
                Are you sure you want to delete the project <span className="font-bold text-gray-900">{selectedInnovationProject?.project}</span>?
              </p>
              <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 bg-gray-50 border-t">
              <button
                onClick={() => setIsInnovationDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#eb4a4a]"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteInnovation}
                className="px-4 py-2 text-sm font-medium text-white bg-[#eb4a4a] rounded-md hover:bg-[#d93a3a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Update Details Modal */}
      {selectedUpdateView && !isAddUpdateModalOpen && !isUpdateDeleteModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          onClick={() => setSelectedUpdateView(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Update Details
              </h3>
              <button
                onClick={() => setSelectedUpdateView(null)}
                className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-md transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">Update Date</p>
                  <p className="mt-1 text-gray-900 font-medium">{formatUpdateDateForDisplay(selectedUpdateView.updateDate)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">ID Project Ref</p>
                  <p className="mt-1 text-gray-900 font-medium">{selectedUpdateView.idProjectRef}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">Created By</p>
                  <p className="mt-1 text-gray-900 font-medium">{selectedUpdateView.createdByEmail || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">Updated By</p>
                  <p className="mt-1 text-gray-900 font-medium">{selectedUpdateView.updatedByEmail || selectedUpdateView.createdByEmail || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">New Status</p>
                  <p className="mt-1 text-gray-900 font-medium">{selectedUpdateView.newStatus || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">Pending Department</p>
                  <p className="mt-1 text-gray-900 font-medium">{selectedUpdateView.pendingDepartment || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">PLC Stage</p>
                  <p className="mt-1 text-gray-900 font-medium">{selectedUpdateView.plcStage || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">Development Progress</p>
                  <p className="mt-1 text-gray-900 font-medium">
                    {selectedUpdateView.developmentProgress !== undefined ? `${selectedUpdateView.developmentProgress}%` : '-'}
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase">Technical Note</p>
                <div className="mt-2 p-3 bg-gray-50 rounded-lg text-gray-700 whitespace-pre-wrap min-h-[80px] max-h-[200px] overflow-y-auto break-words">
                  {selectedUpdateView.technicalNote || <span className="text-gray-400 italic">No technical notes provided.</span>}
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100 flex flex-col gap-6">
                <div className="flex flex-col items-center text-center">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Attached File / Photo</p>
                  
                  {selectedUpdateView.files && selectedUpdateView.files.length > 0 ? (
                    <div className="flex flex-col gap-2 w-full items-center">
                      {selectedUpdateView.files.map((file: any, idx: number) => (
                        <div key={`viewer-file-modal-${idx}`} className="flex items-center gap-3 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg p-2 shadow-sm w-fit max-w-full">
                          <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span className="truncate font-medium max-w-[200px]">{file.name}</span>
                          <div className="flex items-center gap-1 border-l border-gray-200 pl-2">
                            <button
                              type="button"
                              onClick={() => window.open(file.url, '_blank')}
                              className="p-1.5 text-gray-400 hover:text-emerald-600 rounded-md transition-colors"
                              title="View"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-xs italic">No file attached</p>
                  )}
                </div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-between">
              <button
                onClick={() => setIsUpdateDeleteModalOpen(true)}
                className="px-5 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-all shadow-sm flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete Update
              </button>
              <button
                onClick={() => {
                  setInnovationUpdateForm({
                    updateDate: sapDateTimeToInputValue(selectedUpdateView.updateDate) || getCurrentLocalDateTimeInputValue(),
                    updatedByEmail: selectedUpdateView.updatedByEmail || selectedUpdateView.createdByEmail || leadTechnicianEmail,
                    newStatus: selectedUpdateView.newStatus || updateDropdownConfig.statusNamesById[selectedUpdateView.idStatus] || '',
                    pendingDepartment: selectedUpdateView.pendingDepartment || updateDropdownConfig.departmentNamesById[selectedUpdateView.idDepartment] || '',
                    plcStage: selectedUpdateView.plcStage || updateDropdownConfig.plcStageNamesById[selectedUpdateView.idPLC] || '',
                    developmentProgress: selectedUpdateView.developmentProgress || 0,
                    technicalNote: selectedUpdateView.technicalNote || '',
                    originalUpdateDate: selectedUpdateView.id || selectedUpdateView.updateDate || '',
                  });
                  
                  // Recuperar los archivos existentes para mostrarlos en el modal de edición
                  if (selectedUpdateView.files && Array.isArray(selectedUpdateView.files)) {
                    // Creamos objetos File "simulados" para los archivos ya subidos para que la interfaz los renderice
                    // o directamente dejamos que la interfaz maneje selectedUpdateView.files (que ya lo hace)
                    // No necesitamos rellenar selectedUpdateFiles porque eso es solo para archivos *nuevos* pendientes de subir.
                  }
                  
                  // No limpiar selectedUpdateView todavía para que el modal de Add/Edit pueda leer `selectedUpdateView.files`
                  setIsAddUpdateModalOpen(true);
                }}
                className="px-5 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-all shadow-sm flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Update Confirmation Modal */}
      {isUpdateDeleteModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#eb4a4a]" />
                Delete Update
              </h3>
              <button
                onClick={() => setIsUpdateDeleteModalOpen(false)}
                className="text-gray-400 hover:text-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete this update? This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 bg-gray-50 border-t">
              <button
                onClick={() => setIsUpdateDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#eb4a4a] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUpdate}
                className="px-4 py-2 text-sm font-medium text-white bg-[#eb4a4a] rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#eb4a4a] transition-colors"
              >
                Delete Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Success Toast */}
      {showSaveSuccess && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-medium rounded-full shadow-2xl animate-in slide-in-from-bottom-8 fade-in duration-300">
          <Check className="w-5 h-5" />
          Project saved successfully
        </div>
      )}

      {/* Delete File Confirmation Modal */}
      {fileToDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-red-50">
              <h3 className="text-lg font-semibold text-red-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Delete File
              </h3>
              <button
                onClick={() => setFileToDelete(null)}
                className="text-red-400 hover:text-red-600 transition-colors bg-white rounded-full p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 bg-white">
              <p className="text-gray-700 text-sm font-medium">
                Are you sure you want to permanently delete this file?
              </p>
              <p className="text-gray-500 text-sm mt-2">
                This action cannot be undone and the file will be removed from the server immediately.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => setFileToDelete(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteFile}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all shadow-sm flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Warning Modal */}
      {showUnsavedChangesWarning && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Unsaved Changes
              </h3>
              <button
                onClick={() => setShowUnsavedChangesWarning(false)}
                className="text-gray-400 hover:text-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600">
                Are you sure you want to go back to the list? You have unsaved changes that will be lost.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 bg-gray-50 border-t">
              <button
                onClick={() => setShowUnsavedChangesWarning(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#eb4a4a] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDiscardChanges}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors"
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
